(() => {
  const money = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const normalizeWeightToLb = (value, unit) => {
    const weight = Number(value || 0);
    switch (String(unit || '').toLowerCase()) {
      case 'kg':
      case 'kilograms': return weight * 2.2046226218;
      case 'g':
      case 'grams': return weight * 0.0022046226;
      case 'oz':
      case 'ounces': return weight / 16;
      default: return weight;
    }
  };

  const normalizeApplicationUnit = unit => String(unit || '').trim().toLowerCase();

  const convertApplicationAmount = (value, fromUnit, toUnit) => {
    const amount = Number(value || 0);
    const from = normalizeApplicationUnit(fromUnit);
    const to = normalizeApplicationUnit(toUnit);
    if (!amount || !from || !to) return 0;
    if (from === to) return amount;

    const volumeToFlOz = {
      'fl oz': 1,
      qt: 32,
      gal: 128,
      ml: 0.0338140227,
      l: 33.8140227,
    };
    const massToLb = {
      lb: 1,
      oz: 1 / 16,
    };

    if (volumeToFlOz[from] && volumeToFlOz[to]) {
      return (amount * volumeToFlOz[from]) / volumeToFlOz[to];
    }
    if (massToLb[from] && massToLb[to]) {
      return (amount * massToLb[from]) / massToLb[to];
    }
    return 0;
  };

  const applicationBasisSqft = basis => {
    const normalized = String(basis || '').toLowerCase().replace(/,/g, '').trim();
    if (normalized.includes('1000 sq ft')) return 1000;
    if (normalized.includes('100 sq ft')) return 100;
    if (normalized.includes('acre')) return 43560;
    return 0;
  };

  class ProductV3 {
    constructor(root) {
      this.root = root;
      this.productId = String(root.dataset.productId || '');
      this.resourceRoot = this.productId
        ? document.querySelector(`[data-v3-product-resources][data-product-id="${CSS.escape(this.productId)}"]`) || root
        : root;
      this.currency = root.dataset.currency || 'USD';
      this.measureOnly = root.dataset.v3MeasureOnly === 'true';
      this.hasApplicationRate = root.dataset.v3HasApplicationRate === 'true';
      this.hasDilutionRate = root.dataset.v3DilutionRate === 'true';
      this.rateMin = Number(root.dataset.rateMin || 4);
      this.rateMax = Number(root.dataset.rateMax || 5);
      this.wholesaleLb = Number(root.dataset.wholesaleLb || 1000);
      this.baseAtcText = String(root.dataset.atcText || 'Add to Cart');
      this.hasInteracted = false;
      this.selectedVariantId = String(root.dataset.initialVariantId || '');
      this.incomingVariantId = this.selectedVariantId;
      this.hasIncomingVariantParam = new URL(window.location.href).searchParams.has('variant');
      this.lastSavedSqft = null;
      this.lawnSizeTouched = false;
      this.lawnSizeSaveTimer = null;
      this.pendingLawnSizeSave = 0;
      this.lawnSizeSavePromise = Promise.resolve();
      this.lawnSizeSubmitBypass = false;
      this.galleryScrollFrame = null;
      this.galleryVariantId = '';

      this.variants = this.readVariants();
      this.pricingRules = this.readPricingRules();
      this.quickVariants = this.variants.filter(
        variant => !this.pricingRules.excludedVariantIds.has(String(variant.id)),
      );
      if (!this.quickVariants.length) this.quickVariants = this.variants;
      this.plannerVariants = this.quickVariants.filter(variant => variant.available);
      if (!this.plannerVariants.length) {
        this.plannerVariants = this.variants.filter(variant => variant.available);
      }
      this.applicationRates = this.readApplicationRates();
      this.formatConfig = this.readFormatConfig();
      this.selectedFormat = this.formatForVariant(this.variants.find(v => v.id === this.selectedVariantId)) || 'granular';
      this.spreaderData = this.readSpreaderData();
      this.mixingData = this.readMixingData();
      this.upsellData = this.readUpsellData();
      this.upsellRecommendation = null;
      this.selectedApplicationRateIndex = 0;
      if (this.applicationRates.length) {
        this.rateMin = Number(this.applicationRates[0].min || this.rateMin);
        this.rateMax = Number(this.applicationRates[0].max || this.rateMax);
      }
      this.slider = root.querySelector('[data-v3-sqft-range]');
      this.rangeWrap = root.querySelector('[data-v3-range-wrap]');
      this.input = root.querySelector('[data-v3-sqft-input]');
      this.recommendation = root.querySelector('[data-v3-recommendation]');
      this.recommendationPackage = root.querySelector('[data-v3-recommendation-package]');
      this.recommendationTitle = root.querySelector('[data-v3-recommendation-title]');
      this.recommendationDetail = root.querySelector('[data-v3-recommendation-detail]');
      this.recommendationListTotal = root.querySelector('[data-v3-recommendation-list-total]');
      this.recommendationTotal = root.querySelector('[data-v3-recommendation-total]');
      this.recommendationSavings = root.querySelector('[data-v3-recommendation-savings]');
      this.recommendationNote = root.querySelector('[data-v3-recommendation-note]');
      this.upsellToggle = root.querySelector('[data-v3-upsell-toggle]');
      this.upsellDetailTriggers = Array.from(root.querySelectorAll('[data-v3-upsell-details]'));
      this.upsellDialog = root.querySelector('[data-v3-upsell-dialog]');
      this.upsellDialogClose = root.querySelector('[data-v3-upsell-dialog-close]');
      this.upsellModalAdd = root.querySelector('[data-v3-upsell-modal-add]');
      this.upsellModalSummary = root.querySelector('[data-v3-upsell-modal-summary]');
      this.upsellModalPrice = root.querySelector('[data-v3-upsell-modal-price]');
      this.upsellDialogTrigger = null;
      this.price = root.querySelector('[data-v3-price]');
      this.priceCurrent = root.querySelector('[data-v3-price-current]');
      this.priceCompare = root.querySelector('[data-v3-price-compare]');
      this.reviews = root.querySelector('.product-v3__reviews');
      this.reviewCountSync = root.querySelector('[data-v3-review-count-sync]');
      this.reviewSyncObserver = null;
      this.reviewSyncFrame = null;
      this.variantIdInput = root.querySelector('[data-v3-variant-id]');
      this.quantityInput = root.querySelector('[data-v3-quantity]');
      this.applicationTypePropertyInput = root.querySelector('[data-v3-application-type-property]');
      this.purchaseMethodPropertyInput = root.querySelector('[data-v3-purchase-method-property]');
      this.applicationRateButtons = Array.from(root.querySelectorAll('[data-v3-application-rate]'));
      this.spreaderRoot = this.resourceRoot.querySelector('[data-v3-spreader-settings]');
      this.spreaderManufacturer = this.resourceRoot.querySelector('[data-v3-spreader-manufacturer]');
      this.spreaderModel = this.resourceRoot.querySelector('[data-v3-spreader-model]');
      this.spreaderPlaceholder = this.resourceRoot.querySelector('[data-v3-spreader-placeholder]');
      this.spreaderResultList = this.resourceRoot.querySelector('[data-v3-spreader-result-list]');
      this.mixingRoot = this.resourceRoot.querySelector('[data-v3-mixing-guide]');
      this.mixingArea = this.resourceRoot.querySelector('[data-v3-mixing-area]');
      this.mixingProduct = this.resourceRoot.querySelector('[data-v3-mixing-product]');
      this.mixingWater = this.resourceRoot.querySelector('[data-v3-mixing-water]');
      this.mixingDirection = this.resourceRoot.querySelector('[data-v3-mixing-direction]');
      this.buyModeButtons = Array.from(root.querySelectorAll('[data-v3-buy-mode]'));
      this.buyPanels = Array.from(root.querySelectorAll('[data-v3-buy-panel]'));
      this.purchasePlanner = root.querySelector('[data-v3-purchase-planner]');
      this.defaultBuyMode = this.purchasePlanner?.dataset.v3DefaultBuyMode === 'guided' ? 'guided' : 'quick';
      this.buyMode = this.defaultBuyMode;
      this.guidedInitialized = false;
      this.quickPackageSection = root.querySelector('.product-v3__quick-package-section');
      this.quickPackageHeadingRow = root.querySelector('.product-v3__quick-package-heading-row');
      this.quickMeasureLink = root.querySelector('.product-v3__quick-measure-link');
      this.quickPackages = root.querySelector('[data-v3-quick-packages]');
      this.formatPicker = root.querySelector('[data-v3-format-choices]');
      this.quickQty = root.querySelector('[data-v3-quick-qty]');
      this.quickQtyDecrease = root.querySelector('[data-v3-quick-qty-decrease]');
      this.quickQtyIncrease = root.querySelector('[data-v3-quick-qty-increase]');
      this.sqftDisplay = root.querySelector('[data-v3-sqft-display-value]');
      this.quickPackageKey = '';
      this.quickPackageOptionsCache = [];
      this.quickCoverageSqft = 0;
      this.legacyIncomingSelection = null;
      this.upsellSummary = root.querySelector('[data-v3-upsell-summary]');
      this.upsellPrice = root.querySelector('[data-v3-upsell-price]');
      this.atc = root.querySelector('[data-v3-atc]');
      this.atcText = root.querySelector('[data-v3-atc-text]');
      this.atcMain = root.querySelector('[data-v3-atc-main]');
      this.atcSeparator = root.querySelector('[data-v3-atc-separator]');
      this.atcPrice = root.querySelector('[data-v3-atc-price]');
      this.sliderHintDismissed = false;
      this.bulk = root.querySelector('[data-v3-bulk]');

      this.gallery = root.querySelector('[data-v3-gallery]');
      this.thumbViewport = root.querySelector('[data-v3-thumbs]');
      this.thumbPrev = root.querySelector('[data-v3-thumb-prev]');
      this.thumbNext = root.querySelector('[data-v3-thumb-next]');
      this.galleryPrev = root.querySelector('[data-v3-gallery-prev]');
      this.galleryNext = root.querySelector('[data-v3-gallery-next]');
      this.measureTriggers = Array.from(root.querySelectorAll('[data-v3-measure]'));
      this.measureDialog = root.querySelector('[data-v3-measure-dialog]');
      this.documentDialog = root.querySelector('[data-v3-document-dialog]');
      this.documentTitle = root.querySelector('[data-v3-document-modal-title]');
      this.documentImage = root.querySelector('[data-v3-document-image]');
      this.documentFrame = root.querySelector('[data-v3-document-frame]');
      this.documentOpen = root.querySelector('[data-v3-document-open]');
      this.documentClose = root.querySelector('[data-v3-document-close]');
      this.documentTrigger = null;
      this.measureShell = root.querySelector('.product-v3__measure-shell');
      this.measureClose = root.querySelector('[data-v3-measure-close]');
      this.measureMapElement = root.querySelector('[data-v3-measure-map]');
      this.measureAddressInput = root.querySelector('[data-v3-measure-address]');
      this.measureSuggestions = root.querySelector('[data-v3-measure-suggestions]');
      this.measureStart = root.querySelector('[data-v3-measure-start]');
      this.measureUndo = root.querySelector('[data-v3-measure-undo]');
      this.measureClear = root.querySelector('[data-v3-measure-clear]');
      this.measureChangeAddress = root.querySelector('[data-v3-measure-change-address]');
      this.measureHelp = root.querySelector('[data-v3-measure-help]');
      this.measureError = root.querySelector('[data-v3-measure-error]');
      this.measuredSizeLabel = root.querySelector('[data-v3-measured-size]');
      this.useMeasuredSize = root.querySelector('[data-v3-use-measured-size]');
      this.measureMap = null;
      this.measureProjectionOverlay = null;
      this.measurePointerMoveHandler = null;
      this.measureAutocompleteService = null;
      this.measurePlacesService = null;
      this.measureSuggestionItems = [];
      this.measureSuggestionTimer = null;
      this.measureSuggestionRequestId = 0;
      this.measureAreas = [];
      this.measureAreaId = 0;
      this.measureMode = 'idle';
      this.measureDraftLine = null;
      this.measureDraftPoints = [];
      this.measureDraftMarkers = [];
      this.measureClickListener = null;
      this.measureMoveListener = null;
      this.measureGuideLine = null;
      this.measureAddressExpanded = true;
      this.measureReadyPromise = null;
      this.measureSyncTimer = null;
      this.lastRenderedMeasuredSqft = null;
      this.pendingMeasuredSqft = 0;
      this.form = root.querySelector('.product-v3__form');
      this.formError = root.querySelector('.product-form__error-message');
      this.currentRecommendation = null;
      this.slides = Array.from(root.querySelectorAll('[data-v3-media-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-v3-media-thumb]'));
      this.galleryMediaIndexById = new Map(this.slides.map((slide, index) => [String(slide.dataset.mediaId || ''), index]).filter(([id]) => id));

      this.bind();
      if (this.measureOnly) return;
      this.initializeSpreaderSettings();
      this.initializeMixingGuide();
      this.renderIncomingVariant();
      this.renderFormatPicker();
      this.syncFormatResources();
      this.renderQuickPackages();
      if (this.defaultBuyMode === 'quick') {
        this.applyQuickSelection();
      }
      this.selectBuyMode(this.defaultBuyMode, { applySelection: false });
      this.syncVariantGallery(this.selectedVariantId, false);
    }

    readVariants() {
      const node = this.root.querySelector('[data-v3-variants-json]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || '[]')
          .map(variant => ({
            ...variant,
            id: String(variant.id),
            priceCents: Math.round(Number(variant.price || 0) * 100),
            compareAtPriceCents: Math.round(Number(variant.compareAtPrice || 0) * 100),
            weightLb: normalizeWeightToLb(variant.weight, variant.weightUnit),
            contentAmount: Number(variant.contentAmount || 0),
            contentUnit: String(variant.contentUnit || ''),
          }))
          .sort((a, b) => {
            if (!this.hasApplicationRate) return 0;
            const aMeasure = a.contentAmount > 0 ? a.contentAmount : a.weightLb;
            const bMeasure = b.contentAmount > 0 ? b.contentAmount : b.weightLb;
            return aMeasure - bMeasure;
          });
      } catch (error) {
        console.warn('[Product V3] Unable to read variants', error);
        return [];
      }
    }

    readPricingRules() {
      const emptyRules = {
        excludedVariantIds: new Set(),
        legacyVariantMappings: [],
        quantityDiscounts: [],
      };
      const node = this.root.querySelector('[data-v3-pricing-rules-json]');
      if (!node) return emptyRules;

      try {
        const parsed = JSON.parse(node.textContent || '{}');
        const excludedVariantIds = new Set(
          (parsed.excludedVariantIds || []).map(id => String(id)),
        );
        const legacyVariantMappings = (parsed.legacyVariantMappings || [])
          .map(mapping => ({
            fromVariantId: String(mapping.fromVariantId || ''),
            displayWeightLb: Number(mapping.displayWeightLb || 0),
            lines: (mapping.lines || [])
              .map(line => ({
                variantId: String(line.variantId || ''),
                quantity: Math.max(1, Number.parseInt(line.quantity, 10) || 1),
              }))
              .filter(line => line.variantId),
          }))
          .filter(mapping => mapping.fromVariantId && mapping.lines.length);
        const quantityDiscounts = (parsed.quantityDiscounts || [])
          .map(rule => ({
            variantId: String(rule.variantId || ''),
            minimumQuantity: Math.max(1, Number.parseInt(rule.minimumQuantity, 10) || 1),
            discountPerItemCents: Math.max(0, Number.parseInt(rule.discountPerItemCents, 10) || 0),
            label: String(rule.label || ''),
          }))
          .filter(rule => rule.variantId && rule.discountPerItemCents > 0);

        return {
          excludedVariantIds,
          legacyVariantMappings,
          quantityDiscounts,
        };
      } catch (error) {
        console.warn('[Product V3] Unable to read pricing rules', error);
        return emptyRules;
      }
    }

    legacyVariantMapping(variantId) {
      return this.pricingRules.legacyVariantMappings.find(
        mapping => mapping.fromVariantId === String(variantId),
      ) || null;
    }

    calculateLinePricing(lines) {
      const listPriceCents = lines.reduce(
        (sum, line) => sum + (line.variant.priceCents * line.quantity),
        0,
      );
      let discountCents = 0;

      this.pricingRules.quantityDiscounts.forEach(rule => {
        const quantity = lines
          .filter(line => String(line.variant.id) === rule.variantId)
          .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
        if (quantity >= rule.minimumQuantity) {
          discountCents += quantity * rule.discountPerItemCents;
        }
      });

      discountCents = Math.min(listPriceCents, Math.max(0, discountCents));

      return {
        listPriceCents,
        discountCents,
        finalPriceCents: listPriceCents - discountCents,
      };
    }

    readFormatConfig() {
      const node = this.root.querySelector('[data-v3-format-config]');
      if (!node) return null;
      try { return JSON.parse(node.textContent || 'null'); } catch (_) { return null; }
    }
    formatForVariant(variant) {
      if (!variant || !this.formatConfig) return '';
      const title = String(variant.title || '').toLowerCase();
      return title.includes('sprayable') ? 'sprayable' : title.includes('granular') ? 'granular' : '';
    }
    formatVariants(format = this.selectedFormat) {
      return this.quickVariants.filter(variant => this.formatForVariant(variant) === format);
    }
    rateForVariant(variant) {
      const config = this.formatConfig?.[this.formatForVariant(variant)];
      return config ? {min:Number(config.min),max:Number(config.max),unit:'lb',basis:'1,000 sq ft',label:config.label} : this.activeApplicationRate();
    }
    renderFormatPicker() {
      if (!this.formatPicker || !this.formatConfig) return;
      this.formatPicker.replaceChildren();
      ['granular','sprayable'].forEach(format => {
        if (!this.formatVariants(format).length) return;
        const config = this.formatConfig[format];
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'product-v3__format-choice';
        button.dataset.v3Format = format; button.setAttribute('aria-pressed',String(format===this.selectedFormat));
        const check=document.createElement('span');check.className='product-v3__format-check';check.textContent='✓';check.setAttribute('aria-hidden','true');
        const title=document.createElement('span');title.className='product-v3__format-choice-title';title.textContent=config.label;
        const help=document.createElement('span');help.className='product-v3__format-choice-help';help.textContent=config.help;
        button.append(check,title,help);
        button.addEventListener('click',()=>{
          if (this.selectedFormat===format) return;
          this.hasInteracted=true;this.legacyIncomingSelection=null;this.selectedFormat=format;
          const candidate=this.formatVariants(format).find(v=>v.available);
          if (candidate) this.quickPackageKey=`variant:${candidate.id}`;
          this.renderFormatPicker();this.syncFormatResources();this.renderQuickPackages();
          if (this.buyMode==='guided') this.recommend(Number(this.input?.value||this.slider?.value||1000));
          else this.applyQuickSelection();
        });
        this.formatPicker.append(button);
      });
    }
    syncFormatResources() {
      if (!this.formatConfig) return;
      if (this.spreaderRoot) this.spreaderRoot.hidden = this.selectedFormat !== 'granular';
      if (this.mixingRoot) this.mixingRoot.hidden = this.selectedFormat !== 'sprayable';
    }
    readApplicationRates() {
      const node = this.root.querySelector('[data-v3-application-rates-json]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || '[]')
          .map(rate => ({ ...rate, min: Number(rate.min || 0), max: Number(rate.max || 0) }))
          .filter(rate => rate.min > 0 && rate.max > 0);
      } catch (error) {
        console.warn('[Product V3] Unable to read application rates', error);
        return [];
      }
    }

    readSpreaderData() {
      const node = this.resourceRoot.querySelector('[data-v3-spreader-json]');
      if (!node) return null;

      try {
        const parsed = JSON.parse(node.textContent || '{}');
        const rates = (parsed.rates || [])
          .map(rate => ({
            ...rate,
            index: Number(rate.index || 0),
            min: Number(rate.min || 0),
            max: Number(rate.max || 0),
          }))
          .filter(rate => rate.min > 0 && rate.max > 0);

        const calibrations = (parsed.calibrations || [])
          .map(row => ({
            ...row,
            min: Number(row.min || 0),
            max: Number(row.max || 0),
            manufacturer: String(row.manufacturer || '').trim(),
            model: String(row.model || '').trim(),
            name: String(row.name || '').trim(),
            setting: String(row.setting || '').trim(),
          }))
          .filter(row => row.min > 0 && row.max > 0 && row.manufacturer && row.model && row.setting);

        const models = (parsed.models || [])
          .map(item => ({
            manufacturer: String(item.manufacturer || '').trim(),
            model: String(item.model || '').trim(),
            name: String(item.name || '').trim(),
          }))
          .filter(item => item.manufacturer && item.model);

        // The dropdown lists the centrally maintained spreader catalog even
        // if a particular SGN/material has no published dial setting.
        return rates.length && (calibrations.length || models.length)
          ? { rates, calibrations, models, profile: parsed.profile || null }
          : null;
      } catch (error) {
        console.warn('[Product V3] Unable to read spreader settings data', error);
        return null;
      }
    }

    readMixingData() {
      const node = this.resourceRoot.querySelector('[data-v3-mixing-json]');
      if (!node) return null;

      try {
        const parsed = JSON.parse(node.textContent || '{}');
        const carrierVolume = Number(parsed.carrierVolume || 0);
        const carrierUnit = String(parsed.carrierUnit || '').trim();
        const carrierBasis = String(parsed.carrierBasis || '').trim();

        if (carrierVolume <= 0 || !carrierUnit || !applicationBasisSqft(carrierBasis)) return null;
        return { carrierVolume, carrierUnit, carrierBasis };
      } catch (error) {
        console.warn('[Product V3] Unable to read spray mixing data', error);
        return null;
      }
    }

    activeApplicationRate() {
      return this.applicationRates[this.selectedApplicationRateIndex]
        || this.applicationRates[0]
        || null;
    }

    variantAmountForRate(variant, rate) {
      if (!variant || !rate) return 0;
      const unit = normalizeApplicationUnit(rate.unit || 'lb');
      if (unit === 'lb') return Number(variant.weightLb || 0);
      if (unit === 'oz' && Number(variant.contentAmount || 0) <= 0) {
        return Number(variant.weightLb || 0) * 16;
      }
      return convertApplicationAmount(
        variant.contentAmount,
        variant.contentUnit,
        unit,
      );
    }

    linesAmountForRate(lines, rate) {
      return (lines || []).reduce(
        (sum, line) => sum + (this.variantAmountForRate(line.variant, rate) * Number(line.quantity || 0)),
        0,
      );
    }

    coverageRangeForLines(lines, rate, fallbackWeight = 0) {
      if (!rate || Number(rate.min || 0) <= 0 || Number(rate.max || 0) <= 0) return null;
      const basisSqft = applicationBasisSqft(rate.basis);
      if (!basisSqft) return null;

      const unit = normalizeApplicationUnit(rate.unit || 'lb');
      let amount = this.linesAmountForRate(lines, rate);
      if (!amount && unit === 'lb') amount = Number(fallbackWeight || 0);
      if (!amount) return null;

      const minimumSqft = Math.floor((amount / Number(rate.max)) * basisSqft);
      const maximumSqft = Math.floor((amount / Number(rate.min)) * basisSqft);
      if (!maximumSqft) return null;

      return {
        minimumSqft: Math.max(0, minimumSqft),
        maximumSqft,
        liquid: unit !== 'lb',
      };
    }

    readUpsellData() {
      const node = this.root.querySelector('[data-v3-upsell-json]');
      if (!node) return null;

      try {
        const parsed = JSON.parse(node.textContent || '{}');
        const variants = (parsed.variants || [])
          .map(variant => ({
            ...variant,
            id: String(variant.id),
            priceCents: Math.round(Number(variant.price || 0) * 100),
            weightLb: normalizeWeightToLb(variant.weight, variant.weightUnit),
          }))
          .filter(variant => variant.available);

        if (!variants.length) return null;

        const rateMin = Number(parsed.rateMin || 0);
        const rateMax = Number(parsed.rateMax || 0);
        const rateUnit = String(parsed.rateUnit || '').trim().toLowerCase();
        const usesWeightRate = rateMin > 0 && rateMax > 0 && rateUnit === 'lb';

        if (usesWeightRate) {
          variants.sort((a, b) => a.weightLb - b.weightLb);
        }

        return {
          title: String(parsed.title || 'Recommended product'),
          rateMin,
          rateMax,
          rateUnit,
          usesWeightRate,
          variants,
        };
      } catch (error) {
        console.warn('[Product V3] Unable to read upsell product data', error);
        return null;
      }
    }

    bind() {
      const sync = (value, source) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue) || numericValue <= 0) return;

        const sqft = Math.max(1, Math.round(numericValue));
        this.hasInteracted = true;
        this.stopSliderHint(true);

        if (source !== 'slider' && this.slider) {
          this.slider.value = Math.min(sqft, Number(this.slider.max || 30000));
        }
        if (source !== 'input' && this.input) this.input.value = sqft;
        this.renderSqftValue(sqft);
        this.updateSliderHint();

        this.queueLawnSizeSave(sqft);
        this.recommend(sqft);
        this.updateMixingGuide(sqft);
      };

      // Yotpo can stop clicks on its stars before a bubbling listener sees them.
      // Handle the full rating row during capture so the stars and count always
      // take shoppers to the reviews section, regardless of block placement.
      this.reviews?.addEventListener('click', event => {
        const reviewTarget = document.querySelector('[data-v3-main-reviews]')
          || document.querySelector('.section-ls-v3-reviews')
          || document.querySelector('[data-yotpo-instance-id="1224693"]');
        if (!reviewTarget) return;

        event.preventDefault();
        event.stopPropagation();
        reviewTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, true);

      this.syncReviewCountFromMainWidget();

      this.slider?.addEventListener('pointerdown', () => this.stopSliderHint(true), { once: true });
      this.slider?.addEventListener('input', event => sync(event.target.value, 'slider'));
      this.slider?.addEventListener('change', async event => {
        sync(event.target.value, 'slider');
        await this.flushLawnSizeSave();
      });
      this.input?.addEventListener('change', async event => {
        sync(event.target.value, 'input');
        await this.flushLawnSizeSave();
      });
      this.input?.addEventListener('focus', event => event.target.select());
      this.input?.addEventListener('keydown', async event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          sync(event.target.value, 'input');
          await this.flushLawnSizeSave();
          event.target.blur();
        }
      });

      this.applicationRateButtons.forEach(button => {
        button.addEventListener('click', () => this.selectApplicationRate(Number(button.dataset.rateIndex || 0)));
      });

      this.spreaderManufacturer?.addEventListener('change', () => {
        this.renderSpreaderModels();
      });

      this.spreaderModel?.addEventListener('change', () => {
        this.renderSpreaderResult();
      });

      this.buyModeButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (button.dataset.v3BuyMode === 'guided') {
            button.classList.add('is-pulse-dismissed');
          }
          this.selectBuyMode(String(button.dataset.v3BuyMode || 'quick'));
        });
      });

      this.quickPackages?.addEventListener('click', event => {
        const button = event.target.closest('[data-v3-quick-package]');
        if (!button) return;
        this.hasInteracted = true;
        this.legacyIncomingSelection = null;
        this.quickPackageKey = String(button.dataset.v3QuickPackage || '');
        if (this.quickQty) this.quickQty.value = '1';
        this.renderQuickPackages();
        this.applyQuickSelection();
      });

      this.quickQtyDecrease?.addEventListener('click', () => {
        this.legacyIncomingSelection = null;
        this.changeQuickQuantity(-1);
      });
      this.quickQtyIncrease?.addEventListener('click', () => {
        this.legacyIncomingSelection = null;
        this.changeQuickQuantity(1);
      });
      this.quickQty?.addEventListener('input', () => {
        this.legacyIncomingSelection = null;
        this.applyQuickSelection();
      });

      this.upsellToggle?.addEventListener('change', () => {
        const sqft = this.upsellSqftContext();
        this.updateUpsellRecommendation(sqft);
        this.updateAtcLabel();
        this.syncUpsellModalState();
      });

      this.upsellDetailTriggers.forEach(trigger => {
        trigger.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          this.upsellDialogTrigger = trigger;
          this.openUpsellDialog();
        });
      });
      this.upsellDialogClose?.addEventListener('click', () => this.closeUpsellDialog());
      this.upsellDialog?.addEventListener('click', event => {
        if (event.target === this.upsellDialog) this.closeUpsellDialog();
      });
      this.upsellDialog?.addEventListener('close', () => this.resetUpsellDialog());
      this.upsellModalAdd?.addEventListener('click', () => {
        if (this.upsellToggle && !this.upsellToggle.checked) {
          this.upsellToggle.checked = true;
          this.upsellToggle.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.syncUpsellModalState();
      });

      this.form?.addEventListener('submit', event => this.handleMixedSubmit(event), true);

      this.thumbs.forEach(button => {
        button.addEventListener('click', () => {
          const index = Number(button.dataset.mediaIndex || 0);
          this.scrollToSlide(index);
        });
      });

      this.gallery?.addEventListener('scroll', () => {
        if (this.galleryScrollFrame) cancelAnimationFrame(this.galleryScrollFrame);
        this.galleryScrollFrame = requestAnimationFrame(() => this.syncGalleryThumb());
      }, { passive: true });

      const scrollThumbs = direction => {
        if (!this.thumbViewport) return;
        const thumb = this.thumbs[0];
        const styles = window.getComputedStyle(this.thumbViewport);
        const vertical = window.innerWidth > 900;
        const gapValue = vertical ? styles.rowGap : styles.columnGap;
        const gap = Number.parseFloat(gapValue || styles.gap || '0') || 0;
        const rect = thumb?.getBoundingClientRect();
        const amount = (vertical ? rect?.height : rect?.width) || 74;

        if (vertical) {
          this.thumbViewport.scrollBy({ top: direction * (amount + gap), behavior: 'smooth' });
        } else {
          this.thumbViewport.scrollBy({ left: direction * (amount + gap), behavior: 'smooth' });
        }
      };

      this.thumbPrev?.addEventListener('click', () => scrollThumbs(-1));
      this.thumbNext?.addEventListener('click', () => scrollThumbs(1));
      this.galleryPrev?.addEventListener('click', () => this.moveGallery(-1));
      this.galleryNext?.addEventListener('click', () => this.moveGallery(1));
      this.thumbViewport?.addEventListener('scroll', () => this.updateThumbNav(), { passive: true });
      window.addEventListener('resize', () => {
        this.updateThumbNav();
        this.updateGalleryNav();
      });
      requestAnimationFrame(() => {
        this.updateThumbNav();
        this.updateGalleryNav();
      });

      const documentLinks = new Set([
        ...this.root.querySelectorAll('[data-v3-document-link]'),
        ...this.resourceRoot.querySelectorAll('[data-v3-document-link]'),
      ]);
      documentLinks.forEach(link => {
        link.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const href = String(link.href || '').trim();
          if (!href) return;

          event.preventDefault();
          event.stopPropagation();
          this.openDocumentDialog(link);
        });
      });
      this.documentClose?.addEventListener('click', () => this.closeDocumentDialog());
      this.documentDialog?.addEventListener('click', event => {
        if (event.target === this.documentDialog) this.closeDocumentDialog();
      });
      this.documentDialog?.addEventListener('close', () => this.resetDocumentDialog());

      this.root.querySelectorAll('[data-v3-klaviyo-trigger]').forEach(button => {
        button.addEventListener('click', () => {
          const formId = String(button.dataset.formId || '').trim();
          if (!formId) {
            console.warn('[Product V3] Klaviyo form ID is not configured.');
            return;
          }
          window._klOnsite = window._klOnsite || [];
          window._klOnsite.push(['openForm', formId]);
        });
      });

      this.measureTriggers.forEach(trigger => {
        trigger.addEventListener('click', event => {
          if (trigger.tagName === 'A') event.preventDefault();
          this.openMeasureDialog();
        });
      });
      this.measureClose?.addEventListener('click', () => this.closeMeasureDialog());
      this.measureDialog?.addEventListener('close', () => {
        document.documentElement.classList.remove('product-v3-measure-open');
        document.body?.classList.remove('product-v3-measure-open');
      });
      this.measureAddressInput?.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.hideMeasureSuggestions();
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          if (this.measureSuggestionItems.length && !this.measureSuggestions?.hidden) {
            this.selectMeasureSuggestion(0);
          } else if (this.measureSuggestionItems.length) {
            this.selectMeasureSuggestion(0);
          }
        }
      });
      this.measureAddressInput?.addEventListener('input', event => {
        if (this.measureStart) this.measureStart.disabled = true;
        this.hideMeasureError();
        if (this.measureHelp) this.measureHelp.textContent = 'Keep typing, then choose your address from the list.';
        this.scheduleMeasureSuggestions(event.target.value);
      });
      this.measureAddressInput?.addEventListener('focus', event => {
        const value = String(event.target.value || '').trim();
        if (value.length >= 3) this.scheduleMeasureSuggestions(value);
      });
      this.measureAddressInput?.addEventListener('blur', () => {
        window.setTimeout(() => this.hideMeasureSuggestions(), 180);
      });
      this.measureSuggestions?.addEventListener('pointerdown', event => {
        const button = event.target.closest('[data-v3-measure-suggestion]');
        if (!button) return;
        event.preventDefault();
        this.selectMeasureSuggestion(Number(button.dataset.v3MeasureSuggestion));
      });
      this.measureStart?.addEventListener('click', () => {
        if (this.measureMode === 'drawing') {
          this.finishMeasurementArea();
        } else {
          this.startMeasuring();
        }
      });
      this.measureUndo?.addEventListener('click', () => this.undoMeasurePoint());
      this.measureClear?.addEventListener('click', () => this.clearMeasurement());
      this.measureChangeAddress?.addEventListener('click', () => {
        this.measureAddressExpanded = true;
        this.renderMeasureControls();
        this.refreshMeasurementMapLayout();
        window.setTimeout(() => {
          this.measureAddressInput?.focus();
          this.measureAddressInput?.select();
        }, 0);
      });
      this.useMeasuredSize?.addEventListener('click', () => this.applyMeasuredSize());
    }

    updateSliderHint() {
      if (!this.slider || !this.rangeWrap) return;

      const min = Number(this.slider.min || 0);
      const max = Number(this.slider.max || 100);
      const value = Number(this.slider.value || min);
      const span = Math.max(1, max - min);
      const progress = Math.max(0, Math.min(1, (value - min) / span));
      const width = this.slider.getBoundingClientRect().width || this.slider.clientWidth || 0;
      const thumbSize = 16;
      const position = width > thumbSize
        ? (thumbSize / 2) + ((width - thumbSize) * progress)
        : width * progress;

      this.rangeWrap.style.setProperty('--v3-slider-hint-left', `${position}px`);
    }

    startSliderHint() {
      if (!this.slider || !this.rangeWrap || this.sliderHintDismissed) return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      this.rangeWrap.classList.remove('is-hinting');
      window.requestAnimationFrame(() => {
        this.updateSliderHint();
        window.requestAnimationFrame(() => {
          this.rangeWrap?.classList.add('is-hinting');
        });
      });
    }

    stopSliderHint(dismiss = false) {
      if (dismiss) this.sliderHintDismissed = true;
      this.rangeWrap?.classList.remove('is-hinting');
    }

    selectApplicationRate(index) {
      const rate = this.applicationRates[index];
      if (!rate) return;

      this.selectedApplicationRateIndex = index;
      this.rateMin = rate.min;
      this.rateMax = rate.max;
      this.hasInteracted = true;
      this.setApplicationTypeProperty(rate.label || rate.useCase || '');

      this.applicationRateButtons.forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.rateIndex || 0) === index));
      });

      const sqft = Math.max(1, Math.round(Number(this.input?.value || this.slider?.value || 0)));
      if (sqft) {
        this.recommend(sqft);
        this.updateMixingGuide(sqft);
      }
    }

    // One shared SGN/particle-size profile supplies calibrated bands for every
    // product using that profile. A manufacturer-label profile can override it.
    // No dial setting is derived from another material or another spreader model.
    spreaderRateBasisKey(rate) {
      if (!rate) return '';
      return [
        normalizeApplicationUnit(rate.unit),
        String(rate.basis || '').toLowerCase().replace(/,/g, '').trim(),
      ].join('|');
    }

    spreaderHasPublishedSetting(row) {
      const setting = String(row?.setting || '').trim();
      return Boolean(setting && !/^(calibrat(e|ion)|n\/a|unknown|tbd)$/i.test(setting));
    }

    spreaderRateOverlaps(rate, row) {
      if (!rate || !row || this.spreaderRateBasisKey(rate) !== this.spreaderRateBasisKey(row)) {
        return false;
      }
      const rateMin = Number(rate.min);
      const rateMax = Number(rate.max);
      const rowMin = Number(row.min);
      const rowMax = Number(row.max);
      if (![rateMin, rateMax, rowMin, rowMax].every(Number.isFinite)) return false;
      if (rateMin === rateMax) {
        // At the shared boundary, 3 lb uses 3–4, not both 2–3 and 3–4.
        // Fixed manufacturer-label values (e.g. Dylox 3/3) remain exact.
        return (rowMin === rowMax && rowMin === rateMin)
          || (rowMin <= rateMin && rateMin < rowMax);
      }
      return rowMax > rateMin && rowMin < rateMax;
    }

    spreaderBandsForRate(rate, manufacturer, model) {
      if (!this.spreaderData || !rate) return [];
      const matches = this.spreaderData.calibrations.filter(row => (
        row.manufacturer === manufacturer
        && row.model === model
        && this.spreaderHasPublishedSetting(row)
        && this.spreaderRateOverlaps(rate, row)
      ));
      // If an exact-rate setting exists, use it instead of an adjoining
      // SGN interval or a placeholder from older product-specific data.
      if (Number(rate.min) === Number(rate.max)) {
        const target = Number(rate.min);
        const exact = matches.filter(row => Number(row.min) === target
          && Number(row.max) === target);
        if (exact.length) return exact;
        if (!matches.length) {
          // Last configured rate-band endpoint: 10 lb may use the 8–10
          // calibration when no 10–12 band exists. Never mix neighboring
          // bands at interior boundaries.
          return this.spreaderData.calibrations.filter(row => (
            row.manufacturer === manufacturer
            && row.model === model
            && this.spreaderHasPublishedSetting(row)
            && this.spreaderRateBasisKey(rate) === this.spreaderRateBasisKey(row)
            && Number(row.min) < target
            && Number(row.max) === target
          ));
        }
      }
      return matches.sort((a, b) => Number(a.min) - Number(b.min)
        || Number(a.max) - Number(b.max));
    }

    spreaderRateHasFullCalibration(rate, manufacturer, model) {
      if (!rate) return false;
      const bands = this.spreaderBandsForRate(rate, manufacturer, model);
      if (!bands.length) return false;
      const targetMin = Number(rate.min);
      const targetMax = Number(rate.max);
      if (targetMin === targetMax) return true;
      let coveredUntil = targetMin;
      for (const band of bands) {
        if (Number(band.min) > coveredUntil + 0.0001) break;
        coveredUntil = Math.max(coveredUntil, Number(band.max));
        if (coveredUntil >= targetMax - 0.0001) return true;
      }
      return false;
    }

    spreaderRowsForRate(index = 0) {
      if (!this.spreaderData) return [];
      const rate = this.spreaderData.rates.find(item => Number(item.index) === Number(index))
        || this.spreaderData.rates[0];
      if (!rate) return [];

      const models = this.spreaderData.models?.length
        ? this.spreaderData.models
        : this.spreaderData.calibrations;
      const uniqueModels = new Map();
      models.forEach(model => {
        if (model.manufacturer && model.model) {
          uniqueModels.set(`${model.manufacturer}|${model.model}`, model);
        }
      });
      return [...uniqueModels.values()].flatMap(model => {
        const rows = this.spreaderBandsForRate(rate, model.manufacturer, model.model);
        if (!rows.length || !this.spreaderRateHasFullCalibration(rate, model.manufacturer, model.model)) {
          return [];
        }
        const first = rows[0];
        const last = rows[rows.length - 1];
        const settingMin = String(first.settingMin || first.setting || '').trim();
        const settingMax = String(last.settingMax || last.setting || '').trim();
        const setting = settingMin && settingMax && settingMin !== settingMax
          ? `${settingMin}–${settingMax}`
          : (settingMin || settingMax);
        return [{
          ...first,
          manufacturer: model.manufacturer,
          model: model.model,
          name: model.name || first.name,
          min: Number(rate.min),
          max: Number(rate.max),
          setting,
        }];
      });
    }


    spreaderEstimateAdjacentLowerBand(rate, manufacturer, model, partial) {
      // Shared SGN estimate only. Never treat an extrapolation as an actual
      // calibrated setting, or extend beyond a single adjacent rate band.
      if (!partial || this.spreaderData?.profile?.source !== 'sgn') return null;
      const start = Number(partial.coveredMin);
      const targetMin = Number(rate?.min);
      if (!Number.isFinite(start) || !Number.isFinite(targetMin)
        || targetMin >= start - 0.0001) return null;

      const sameModel = this.spreaderData.calibrations.filter(row => (
        row.manufacturer === manufacturer && row.model === model
        && this.spreaderHasPublishedSetting(row)
        && this.spreaderRateBasisKey(rate) === this.spreaderRateBasisKey(row)
      ));
      const first = sameModel.find(row => Math.abs(Number(row.min) - start) < 0.0001);
      if (!first) return null;
      const width = Number(first.max) - Number(first.min);
      if (Math.abs(width - 1) > 0.0001 || targetMin < start - width - 0.0001) return null;
      const next = sameModel.find(row => (
        Math.abs(Number(row.min) - Number(first.max)) < 0.0001
        && Math.abs(Number(row.max) - (Number(first.max) + width)) < 0.0001
      ));
      if (!next) return null;

      const numeric = [first.settingMin, first.settingMax, next.settingMin, next.settingMax]
        .map(value => String(value ?? '').trim());
      if (numeric.some(value => !/^[0-9]+([.][0-9]+)?$/.test(value))) return null;
      const [firstMin, firstMax, nextMin, nextMax] = numeric.map(Number);
      const stepMin = nextMin - firstMin;
      const stepMax = nextMax - firstMax;
      if (stepMin <= 0 || stepMax <= 0 || stepMin > 3 || stepMax > 3) return null;
      const estimatedMin = firstMin - stepMin;
      const estimatedMax = firstMax - stepMax;
      if (estimatedMin <= 0 || estimatedMax <= 0 || estimatedMin > estimatedMax) return null;

      const fmt = number => Number(number.toFixed(2)).toString();
      return {
        setting: estimatedMin === estimatedMax
          ? fmt(estimatedMin) : `${fmt(estimatedMin)}–${fmt(estimatedMax)}`,
        min: targetMin,
        max: start,
        source: 'extrapolated from adjacent SGN bands',
      };
    }

    spreaderPartialCalibrationForRate(rate, manufacturer, model) {
      // When a label rate extends outside the shared SGN library, show only
      // the portion that HAS a calibration. Never present a partial setting
      // as covering the whole labeled application-rate interval.
      if (!rate || this.spreaderRateHasFullCalibration(rate, manufacturer, model)) return null;
      const bands = this.spreaderBandsForRate(rate, manufacturer, model);
      if (!bands.length) return null;

      let first = null;
      let last = null;
      let coveredMin = 0;
      let coveredMax = 0;
      for (const band of bands) {
        const overlapMin = Math.max(Number(rate.min), Number(band.min));
        const overlapMax = Math.min(Number(rate.max), Number(band.max));
        if (overlapMax <= overlapMin + 0.0001) continue;

        if (!first) {
          first = band;
          last = band;
          coveredMin = overlapMin;
          coveredMax = overlapMax;
        } else if (overlapMin <= coveredMax + 0.0001) {
          last = band;
          coveredMax = Math.max(coveredMax, overlapMax);
        } else {
          // There is an uncalibrated gap. Do not join noncontiguous bands.
          break;
        }
      }
      if (!first) return null;

      const settingMin = String(first.settingMin || first.setting || '').trim();
      const settingMax = String(last.settingMax || last.setting || '').trim();
      const setting = settingMin && settingMax && settingMin !== settingMax
        ? `${settingMin}–${settingMax}`
        : (settingMin || settingMax);
      if (!setting) return null;

      const partial = { row: { ...first, setting }, coveredMin, coveredMax };
      partial.lowerEstimate = this.spreaderEstimateAdjacentLowerBand(rate, manufacturer, model, partial);
      return partial;
    }

    spreaderMultiPassForRate(rate, manufacturer, model) {
      // A dial position is not a linear function of application rate.
      // Display multi-pass instructions only when the product explicitly
      // defines a second fixed-rate option with published calibration.
      if (!this.spreaderData || !rate) return null;
      for (const baseRate of this.spreaderData.rates) {
        const perPassRate = Number(baseRate.min);
        if (Number(baseRate.index) === Number(rate.index)
          || perPassRate <= 0
          || Math.abs(perPassRate - Number(baseRate.max)) > 0.0001
          || this.spreaderRateBasisKey(baseRate) !== this.spreaderRateBasisKey(rate)) continue;
        const minPasses = Number(rate.min) / perPassRate;
        const maxPasses = Number(rate.max) / perPassRate;
        if (minPasses < 2 || maxPasses > 4
          || Math.abs(minPasses - Math.round(minPasses)) > 0.0001
          || Math.abs(maxPasses - Math.round(maxPasses)) > 0.0001) continue;
        const row = this.spreaderRowsForRate(baseRate.index).find(item => (
          item.manufacturer === manufacturer && item.model === model
        ));
        if (!row) continue;
        return { row, baseRate, minPasses: Math.round(minPasses), maxPasses: Math.round(maxPasses) };
      }
      return null;
    }

    spreaderAllRows() {
      if (!this.spreaderData) return [];
      const models = this.spreaderData.models || [];
      if (models.length) {
        return models.filter(row => row.manufacturer && row.model);
      }
      const unique = new Map();
      this.spreaderData.calibrations.forEach(row => {
        if (row.manufacturer && row.model) {
          unique.set(`${row.manufacturer}|${row.model}`, row);
        }
      });
      return [...unique.values()];
    }

    populateSpreaderSelect(select, values, placeholder) {
      if (!select) return;
      select.replaceChildren();

      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      placeholderOption.selected = true;
      placeholderOption.disabled = true;
      select.append(placeholderOption);

      values.forEach(value => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.append(option);
      });

      select.disabled = values.length === 0;
    }

    resetSpreaderResult(message = 'Select your spreader to see the setting.') {
      if (this.spreaderPlaceholder) {
        this.spreaderPlaceholder.textContent = message;
        this.spreaderPlaceholder.hidden = false;
      }
      if (this.spreaderResultList) {
        this.spreaderResultList.replaceChildren();
        this.spreaderResultList.hidden = true;
      }
    }

    initializeSpreaderSettings() {
      if (!this.spreaderRoot || !this.spreaderData) return;

      const rows = this.spreaderAllRows();
      const manufacturers = [...new Set(rows.map(row => row.manufacturer))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      this.populateSpreaderSelect(this.spreaderManufacturer, manufacturers, 'Select Manufacturer');
      this.populateSpreaderSelect(this.spreaderModel, [], 'Select Model');

      this.resetSpreaderResult(
        manufacturers.length
          ? 'Select your spreader to see the setting.'
          : 'No spreader settings are available for this product.',
      );
    }

    renderSpreaderModels() {
      if (!this.spreaderData || !this.spreaderManufacturer) return;

      const manufacturer = String(this.spreaderManufacturer.value || '').trim();
      const models = [...new Set(
        this.spreaderAllRows()
          .filter(row => row.manufacturer === manufacturer)
          .map(row => row.model)
          .filter(Boolean),
      )].sort((a, b) => a.localeCompare(b));

      this.populateSpreaderSelect(this.spreaderModel, models, 'Select Model');
      this.resetSpreaderResult(
        models.length
          ? 'Select a model to see the setting.'
          : 'No models are available for this manufacturer.',
      );
    }

    renderSpreaderResult() {
      if (!this.spreaderData || !this.spreaderManufacturer || !this.spreaderModel || !this.spreaderResultList) return;

      const manufacturer = String(this.spreaderManufacturer.value || '').trim();
      const model = String(this.spreaderModel.value || '').trim();
      if (!manufacturer || !model) {
        this.resetSpreaderResult();
        return;
      }

      // Show every configured rate, even when a direct dial setting is absent.
      const results = this.spreaderData.rates.map(rate => {
        const candidate = this.spreaderRowsForRate(rate.index).find(item => (
          item.manufacturer === manufacturer && item.model === model
        ));
        const row = candidate && this.spreaderRateHasFullCalibration(rate, manufacturer, model)
          ? candidate : null;
        const partial = row ? null : this.spreaderPartialCalibrationForRate(rate, manufacturer, model);
        const multiPass = row || partial ? null : this.spreaderMultiPassForRate(rate, manufacturer, model);
        return { rate, row: row || partial?.row || multiPass?.row || null, partial, multiPass };
      });

      if (this.spreaderPlaceholder) this.spreaderPlaceholder.hidden = true;
      this.spreaderResultList.replaceChildren();
      this.spreaderResultList.hidden = false;

      results.forEach(({ rate, row, partial, multiPass }) => {
        const card = document.createElement('div');
        card.className = 'product-v3__spreader-result-card';

        const context = document.createElement('div');
        context.className = 'product-v3__spreader-result-context';

        const label = document.createElement('span');
        label.className = 'product-v3__spreader-result-label';
        label.textContent = rate?.label || 'Application';

        const rateText = document.createElement('span');
        rateText.className = 'product-v3__spreader-result-rate';
        rateText.textContent = rate?.displayRate || '';
        context.append(label, rateText);

        if (partial || multiPass || !row) {
          const detail = document.createElement('span');
          detail.className = 'product-v3__spreader-result-details';
          if (partial) {
            const shortRate = value => Number(value.toFixed(2)).toString();
            const covered = Math.abs(partial.coveredMin - partial.coveredMax) < 0.0001
              ? shortRate(partial.coveredMin)
              : `${shortRate(partial.coveredMin)}–${shortRate(partial.coveredMax)}`;
            if (partial.lowerEstimate) {
              const lower = `${shortRate(partial.lowerEstimate.min)}–${shortRate(partial.lowerEstimate.max)}`;
              detail.textContent = `Lower rate ${lower} ${rate.unit} per ${rate.basis}: estimated dial ${partial.lowerEstimate.setting}. Higher rate ${covered}: shared SGN starting dial ${partial.row.setting}. The lower setting is extrapolated, not measured or manufacturer-verified. Confirm the product SGN and calibrate with a weighed test area before use.`;
            } else {
              detail.textContent = `Starting setting for ${covered} ${rate.unit} per ${rate.basis} only. Rates outside that range have no shared calibration; calibrate with a weighed test area.`;
            }
          } else if (multiPass) {
            const passes = multiPass.minPasses === multiPass.maxPasses
              ? `${multiPass.minPasses}×`
              : `${multiPass.minPasses}×–${multiPass.maxPasses}×`;
            const perPass = multiPass.baseRate.displayRate || 'the maintenance rate';
            detail.textContent = `${passes} even passes at ${perPass} per pass. Keep the same spreader setting for each pass and calibrate first.`;
          } else {
            detail.textContent = 'No verified setting covers this entire rate. Calibrate with a weighed test area.';
          }
          context.append(detail);
        }

        const setting = document.createElement('strong');
        setting.className = 'product-v3__spreader-result-setting';
        setting.textContent = partial?.lowerEstimate ? `${partial.lowerEstimate.setting} est.` : (row?.setting || 'Calibrate');

        card.append(context, setting);
        this.spreaderResultList.append(card);
      });
    }

    initializeMixingGuide() {
      if (!this.mixingRoot || !this.mixingData) return;
      this.updateMixingGuide(0);
    }

    formatMixingAmount(value, unit) {
      const amount = Number(value || 0);
      const normalized = normalizeApplicationUnit(unit);
      if (!amount || !normalized) return '';

      if (normalized === 'gal') {
        return `${this.trim(amount)} ${Math.abs(amount - 1) < 0.05 ? 'gallon' : 'gallons'}`;
      }
      if (normalized === 'qt') {
        return `${this.trim(amount)} ${Math.abs(amount - 1) < 0.05 ? 'quart' : 'quarts'}`;
      }
      if (normalized === 'l') {
        return `${this.trim(amount)} L`;
      }
      if (normalized === 'ml') {
        return `${this.trim(amount)} mL`;
      }
      return `${this.trim(amount)} ${unit}`;
    }

    updateMixingGuide(sqft = 0) {
      if (!this.mixingRoot || !this.mixingData) return;

      const rate = this.activeApplicationRate();
      if (!rate) return;

      const rateBasisSqft = applicationBasisSqft(rate.basis);
      const carrierBasisSqft = applicationBasisSqft(this.mixingData.carrierBasis);
      if (!rateBasisSqft || !carrierBasisSqft) return;

      const targetSqft = Number(sqft || 0) > 0 ? Number(sqft) : rateBasisSqft;
      const minProduct = (targetSqft / rateBasisSqft) * Number(rate.min || 0);
      const maxProduct = (targetSqft / rateBasisSqft) * Number(rate.max || 0);
      const waterAmount = (targetSqft / carrierBasisSqft) * this.mixingData.carrierVolume;

      const productText = this.formatApplicationRange(minProduct, maxProduct, rate.unit);
      const waterText = this.formatMixingAmount(waterAmount, this.mixingData.carrierUnit);
      const areaText = Number(sqft || 0) > 0
        ? `your ${Math.round(targetSqft).toLocaleString()} sq ft lawn`
        : `${Math.round(targetSqft).toLocaleString()} sq ft`;

      if (this.mixingArea) {
        this.mixingArea.textContent = Number(sqft || 0) > 0
          ? `For your ${Math.round(targetSqft).toLocaleString()} sq ft lawn`
          : `For ${Math.round(targetSqft).toLocaleString()} sq ft`;
      }
      if (this.mixingProduct) this.mixingProduct.textContent = productText;
      if (this.mixingWater) this.mixingWater.textContent = waterText;
      if (this.mixingDirection) {
        this.mixingDirection.textContent = `Mix ${productText} with ${waterText} of water and spray evenly over ${areaText}.`;
      }
    }

    selectBuyMode(mode, options = {}) {
      const nextMode = mode === 'guided' ? 'guided' : 'quick';
      this.buyMode = nextMode;
      if (this.purchasePlanner) this.purchasePlanner.dataset.v3ActiveBuyMode = nextMode;
      this.setPurchaseMethodProperty(nextMode === 'guided' ? 'Find My Amount' : 'Quick Buy');

      this.buyModeButtons.forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.v3BuyMode === nextMode));
      });

      this.buyPanels.forEach(panel => {
        panel.hidden = panel.dataset.v3BuyPanel !== nextMode;
      });

      if (nextMode === 'quick') {
        this.stopSliderHint();
        if (!this.formatConfig) this.setApplicationTypeProperty('');
        this.renderQuickPackages();
        if (options.applySelection !== false) this.applyQuickSelection();
        this.updateMixingGuide(0);
        return;
      }

      const loadedSavedLawn = this.guidedInitialized ? false : this.loadSavedLawn();
      this.guidedInitialized = true;
      if (!loadedSavedLawn) {
        const sqft = Math.max(1, Math.round(Number(this.input?.value || this.slider?.value || 0)));
        if (sqft) {
          this.recommend(sqft);
          this.updateMixingGuide(sqft);
        }
      }
      this.startSliderHint();
    }

    quickPackageOptions() {
      return this.quickVariants
        .map(variant => {
          const lines = [{ variant, quantity: 1 }];
          const pricing = this.calculateLinePricing(lines);
          const discountRule = this.pricingRules.quantityDiscounts.find(
            rule => rule.variantId === variant.id
              && rule.minimumQuantity > 1
              && rule.discountPerItemCents > 0,
          );

          return {
            key: `variant:${variant.id}`,
            lines,
            totalWeight: variant.weightLb,
            totalBags: 1,
            listPriceCents: pricing.listPriceCents,
            discountCents: pricing.discountCents,
            totalPriceCents: pricing.finalPriceCents,
            label: variant.title && variant.title !== 'Default Title'
              ? variant.title
              : variant.contentAmount > 0 && variant.contentUnit
                ? `${this.trim(variant.contentAmount)} ${variant.contentUnit}`
                : this.hasApplicationRate ? this.formatWeight(variant.weightLb) : 'Standard',
            bestValue: Boolean(discountRule),
            badge: String(variant.badge || "").trim(),
            yieldText: String(variant.yieldText || '').trim(),
            available: Boolean(variant.available),
          };
        })
        .sort((a, b) => this.hasApplicationRate
          ? a.totalWeight - b.totalWeight || a.totalPriceCents - b.totalPriceCents
          : 0);
    }

    matchQuickPackageKey(options) {
      if (this.quickPackageKey && options.some(option => option.key === this.quickPackageKey && option.available)) {
        return this.quickPackageKey;
      }

      const currentLines = this.currentRecommendation?.lines || [];
      if (currentLines.length === 1) {
        const currentLine = currentLines[0];
        const matched = options.find(option => (
          option.lines.length === 1
          && option.lines[0].variant.id === currentLine.variant.id
        ));

        if (matched?.available) {
          if (this.quickQty) {
            this.quickQty.value = String(Math.max(1, Number(currentLine.quantity || 1)));
          }
          return matched.key;
        }
      }

      if (this.hasIncomingVariantParam) {
        const mapping = this.legacyVariantMapping(this.incomingVariantId);
        if (mapping?.lines?.length === 1) {
          const mappedLine = mapping.lines[0];
          const matched = options.find(option => (
            option.lines.length === 1
            && option.lines[0].variant.id === mappedLine.variantId
          ));

          if (matched?.available) {
            if (this.quickQty) {
              this.quickQty.value = String(Math.max(1, Number(mappedLine.quantity || 1)));
            }
            return matched.key;
          }
        }
      }

      const selected = options.find(option => (
        option.available
        && option.lines.length === 1
        && option.lines[0].variant.id === this.selectedVariantId
      ));
      return selected?.key || options.find(option => option.available)?.key || '';
    }

    quickCoverageRows(totalWeight, lines = []) {
      if (!this.applicationRates.length) return [];
      const rateRows = this.applicationRates
        .map(rate => {
          if (Number(rate.min || 0) <= 0 || Number(rate.max || 0) <= 0) return null;
          return this.coverageRangeForLines(lines, rate, totalWeight);
        })
        .filter(Boolean);

      if (rateRows.length) {
        const minimumValues = rateRows
          .map(row => Number(row.minimumSqft || 0))
          .filter(value => value > 0);
        const maximumValues = rateRows
          .map(row => Number(row.maximumSqft || 0))
          .filter(value => value > 0);

        if (maximumValues.length) {
          return [{
            minimumSqft: minimumValues.length ? Math.min(...minimumValues) : 0,
            maximumSqft: Math.max(...maximumValues),
            liquid: rateRows.some(row => row.liquid),
          }];
        }
      }

      const fallbackMinimumSqft = this.rateMax > 0
        ? Math.floor((totalWeight / this.rateMax) * 1000)
        : 0;
      const fallbackMaximumSqft = this.rateMin > 0
        ? Math.floor((totalWeight / this.rateMin) * 1000)
        : 0;
      return fallbackMaximumSqft > 0
        ? [{
          minimumSqft: Math.max(0, fallbackMinimumSqft),
          maximumSqft: fallbackMaximumSqft,
          liquid: false,
        }]
        : [];
    }

    buildQuickCoverage(totalWeight, lines = []) {
      const rows = this.quickCoverageRows(totalWeight, lines);
      if (!rows.length) return null;
      const coverage = document.createElement('span');
      coverage.className = 'product-v3__quick-package-coverage';

      rows.forEach(row => {
        const line = document.createElement('span');
        line.className = 'product-v3__quick-package-coverage-line';

        const label = document.createElement('span');
        label.className = 'product-v3__quick-package-coverage-label';
        label.textContent = 'Up to\u00a0';

        const value = document.createElement('span');
        value.className = 'product-v3__quick-package-coverage-value';

        const maximumSqft = Math.max(0, Math.round(Number(row.maximumSqft || 0)));
        value.textContent = `${maximumSqft.toLocaleString()} sq ft`;

        line.append(label, value);
        coverage.append(line);
      });

      return coverage;
    }

    buildQuickYield(yieldText) {
      const text = String(yieldText || '').trim();
      if (!text) return null;

      const coverage = document.createElement('span');
      coverage.className = 'product-v3__quick-package-coverage';

      const line = document.createElement('span');
      line.className = 'product-v3__quick-package-coverage-line';

      const label = document.createElement('span');
      label.className = 'product-v3__quick-package-coverage-label';
      label.textContent = 'Makes\u00a0';

      const value = document.createElement('span');
      value.className = 'product-v3__quick-package-coverage-value';
      value.textContent = text;

      line.append(label, value);
      coverage.append(line);
      return coverage;
    }

    buildQuickPackagePromotion(option) {
      if (!option.badge || !option.available) return null;

      const promotion = document.createElement('span');
      promotion.className = 'product-v3__quick-package-promo';

      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'product-v3__quick-package-promo-icon');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('fill', 'none');
      icon.setAttribute('stroke', 'currentColor');
      icon.setAttribute('stroke-width', '2');
      icon.setAttribute('stroke-linecap', 'round');
      icon.setAttribute('stroke-linejoin', 'round');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<path d="M20.59 13.41 13.41 20.59a2 2 0 0 1-2.83 0l-7.17-7.17A2 2 0 0 1 2 12V4a2 2 0 0 1 2-2h8a2 2 0 0 1 1.41.59l7.18 7.18a2 2 0 0 1 0 2.82Z"/><circle cx="7.5" cy="7.5" r=".75"/>';

      const text = document.createElement('span');
      text.className = 'product-v3__quick-package-promo-text';
      text.textContent = option.badge;

      promotion.append(icon, text);
      return promotion;
    }

    renderQuickPackages() {
      if (!this.quickPackages) return;

      const options = this.quickPackageOptions().filter(option => !this.formatConfig || this.formatForVariant(option.lines[0]?.variant) === this.selectedFormat);
      this.quickPackageOptionsCache = options;
      this.quickPackageKey = this.matchQuickPackageKey(options);

      if (
        this.quickMeasureLink
        && this.quickPackageHeadingRow
        && this.quickMeasureLink.parentElement !== this.quickPackageHeadingRow
      ) {
        this.quickPackageHeadingRow.append(this.quickMeasureLink);
      }

      const singleVariant = options.length === 1 && !this.legacyIncomingSelection;
      this.quickPackageSection?.classList.toggle('is-single-variant', singleVariant);
      this.quickPackages.replaceChildren();
      if (this.formatConfig) {
        this.quickPackages.classList.add('product-v3__format-size-grid');
        options.forEach(option => {
          const button=document.createElement('button');button.type='button';
          button.className='product-v3__format-size-card';button.dataset.v3QuickPackage=option.key;
          button.disabled=!option.available;button.setAttribute('aria-pressed',String(option.available&&option.key===this.quickPackageKey));
          const check=document.createElement('span');check.className='product-v3__format-size-check';check.textContent='✓';check.setAttribute('aria-hidden','true');
          const title=document.createElement('span');title.className='product-v3__format-size-title';
          const match=option.label.match(/(18|50)\s*lbs?/i);title.textContent=match?`${match[1]} lbs.`:option.label;
          const coverage=document.createElement('span');coverage.className='product-v3__format-size-coverage';
          const range=this.coverageRangeForLines(option.lines,this.rateForVariant(option.lines[0]?.variant),option.totalWeight);
          coverage.textContent=range?`Up to ${Math.round(range.maximumSqft).toLocaleString()} sq ft`:'';
          const promotion = this.buildQuickPackagePromotion(option);
          if (promotion) promotion.className = 'product-v3__format-size-promo';

          const price = document.createElement('span');
          price.className = 'product-v3__format-size-price';
          price.textContent = option.available
            ? money(option.totalPriceCents, this.currency)
            : 'Out of Stock';

          button.append(check, title, coverage);
          if (promotion) button.append(promotion);
          button.append(price);
          this.quickPackages.append(button);
        });
        return;
      }
      if (singleVariant) {
        const option = options[0];
        const card = document.createElement('div');
        card.className = 'product-v3__quick-package product-v3__quick-package--single';
        card.classList.toggle('is-unavailable', !option.available);
        card.setAttribute(
          'aria-label',
          option.available
            ? `${option.label}, ${money(option.totalPriceCents, this.currency)}`
            : `${option.label}, out of stock`,
        );

        const media = document.createElement('span');
        media.className = 'product-v3__quick-package-single-media';
        const galleryImage = this.slides[0]?.querySelector('img');
        if (galleryImage) {
          const image = document.createElement('img');
          image.src = galleryImage.currentSrc || galleryImage.src;
          image.alt = '';
          image.loading = 'lazy';
          media.append(image);
        }

        const info = document.createElement('span');
        info.className = 'product-v3__quick-package-single-info';

        const title = document.createElement('strong');
        title.className = 'product-v3__quick-package-title product-v3__quick-package-single-title';
        const weightMatch = String(option.label || '').trim().match(/^([\d.]+)\s*lbs?\.?$/i);
        title.textContent = weightMatch ? `${weightMatch[1]} lb` : option.label;

        const coverage = this.buildQuickCoverage(option.totalWeight, option.lines)
          || (this.hasDilutionRate ? this.buildQuickYield(option.yieldText) : null);
        info.append(title);
        if (coverage) info.append(coverage);
        const promotion = this.buildQuickPackagePromotion(option);
        if (promotion) info.append(promotion);

        const price = document.createElement('strong');
        price.className = 'product-v3__quick-package-price product-v3__quick-package-single-price';
        price.textContent = option.available
          ? money(option.totalPriceCents, this.currency)
          : 'Out of Stock';

        card.append(media, info, price);
        this.quickPackages.append(card);
          return;
      }

      if (this.legacyIncomingSelection) {
        const legacy = this.legacyIncomingSelection;
        const selectedCard = document.createElement('div');
        selectedCard.className = 'product-v3__quick-package product-v3__quick-package--incoming';
        selectedCard.setAttribute('aria-label', `${this.formatWeight(legacy.displayWeightLb)} selected from your shopping link`);

        const selectedBadge = document.createElement('span');
        selectedBadge.className = 'product-v3__quick-package-badge product-v3__quick-package-badge--incoming';
        selectedBadge.textContent = 'Selected from shopping link';

        const selectedTitle = document.createElement('span');
        selectedTitle.className = 'product-v3__quick-package-title';
        selectedTitle.textContent = this.formatWeight(legacy.displayWeightLb);

        const selectedPrice = document.createElement('span');
        selectedPrice.className = 'product-v3__quick-package-price';
        selectedPrice.textContent = money(legacy.priceCents, this.currency);

        const selectedDetail = document.createElement('span');
        selectedDetail.className = 'product-v3__quick-package-incoming-detail';
        selectedDetail.textContent = legacy.packageText;

        const selectedCoverage = this.buildQuickCoverage(legacy.displayWeightLb);

        selectedCard.append(selectedBadge, selectedTitle, selectedPrice, selectedDetail);
        if (selectedCoverage) selectedCard.append(selectedCoverage);
        this.quickPackages.append(selectedCard);
      }

      options.forEach(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-v3__quick-package product-v3__quick-package--option';
        button.dataset.v3QuickPackage = option.key;
        button.disabled = !option.available;
        button.classList.toggle('is-unavailable', !option.available);
        button.setAttribute('aria-disabled', String(!option.available));
        button.setAttribute(
          'aria-pressed',
          String(option.available && !this.legacyIncomingSelection && option.key === this.quickPackageKey),
        );

        const title = document.createElement('span');
        title.className = 'product-v3__quick-package-title';
        title.textContent = option.label;

        const price = document.createElement('span');
        price.className = 'product-v3__quick-package-price';
        price.textContent = option.available
          ? money(option.totalPriceCents, this.currency)
          : 'Out of Stock';

        const coverage = this.buildQuickCoverage(option.totalWeight, option.lines)
          || (this.hasDilutionRate ? this.buildQuickYield(option.yieldText) : null);

        button.append(title, price);
        if (coverage) button.append(coverage);
        const promotion = this.buildQuickPackagePromotion(option);
        if (promotion) button.append(promotion);

        this.quickPackages.append(button);
      });

    }

    selectedQuickPackage() {
      return this.quickPackageOptionsCache.find(option => (
        option.available && option.key === this.quickPackageKey
      ))
        || this.quickPackageOptionsCache.find(option => option.available)
        || null;
    }

    renderSqftValue(sqft) {
      const value = Math.max(1, Math.round(Number(sqft || 0)));
      if (this.input && document.activeElement !== this.input) {
        this.input.value = String(value);
      }
      if (this.sqftDisplay) this.sqftDisplay.textContent = value.toLocaleString();
    }

    changeQuickQuantity(change) {
      if (!this.quickQty) return;
      const current = Math.max(1, Number.parseInt(this.quickQty.value, 10) || 1);
      this.quickQty.value = String(Math.max(1, current + change));
      this.applyQuickSelection();
    }

    applyQuickSelection() {
      if (this.buyMode !== 'quick' && this.buyModeButtons.length) return;

      const option = this.selectedQuickPackage();
      if (!option) return;

      const packageQuantity = Math.max(1, Number.parseInt(this.quickQty?.value || '1', 10) || 1);
      if (this.quickQty) this.quickQty.value = String(packageQuantity);

      const lines = option.lines.map(line => ({
        variant: line.variant,
        quantity: line.quantity * packageQuantity,
      }));
      const totalWeight = option.totalWeight * packageQuantity;
      const primaryRate = this.rateForVariant(lines[0]?.variant);
      const coverageRange = primaryRate
        ? this.coverageRangeForLines(lines, primaryRate, totalWeight)
        : null;
      const coverageSqft = coverageRange?.maximumSqft || 0;

      this.quickCoverageSqft = coverageSqft;
      this.applyRecommendation({
        lines,
        totalWeight,
        totalBags: lines.reduce((sum, line) => sum + line.quantity, 0),
        validRange: true,
        quick: true,
      }, !this.legacyIncomingSelection);

      this.updateUpsellRecommendation(Math.max(1, coverageSqft));
    }

    upsellSqftContext() {
      if (this.buyMode === 'quick' && this.quickCoverageSqft > 0) {
        return this.quickCoverageSqft;
      }
      return Math.max(1, Math.round(Number(this.input?.value || this.slider?.value || 0)));
    }

    setApplicationTypeProperty(value) {
      if (!this.applicationTypePropertyInput) return;
      const normalized = String(value || '').trim();
      this.applicationTypePropertyInput.value = normalized;
      this.applicationTypePropertyInput.disabled = !normalized;
    }

    setPurchaseMethodProperty(value) {
      if (!this.purchaseMethodPropertyInput) return;
      const normalized = String(value || '').trim();
      this.purchaseMethodPropertyInput.value = normalized;
      this.purchaseMethodPropertyInput.disabled = !normalized;
    }

    openUpsellDialog() {
      if (!this.upsellDialog || typeof this.upsellDialog.showModal !== 'function') return;

      this.syncUpsellModalState();
      document.documentElement.classList.add('product-v3-upsell-open');
      document.body?.classList.add('product-v3-upsell-open');
      this.upsellDialog.showModal();
      this.upsellDialogClose?.focus();
    }

    closeUpsellDialog() {
      if (this.upsellDialog?.open) this.upsellDialog.close();
    }

    resetUpsellDialog() {
      document.documentElement.classList.remove('product-v3-upsell-open');
      document.body?.classList.remove('product-v3-upsell-open');

      const trigger = this.upsellDialogTrigger;
      this.upsellDialogTrigger = null;
      if (trigger && document.contains(trigger)) trigger.focus();
    }

    syncUpsellModalState() {
      if (!this.upsellModalAdd) return;

      const isAdded = Boolean(this.upsellToggle?.checked);
      const addLabel = String(this.upsellModalAdd.dataset.addLabel || 'Add to order').trim();
      const addedLabel = String(this.upsellModalAdd.dataset.addedLabel || 'Added to order').trim();

      this.upsellModalAdd.textContent = isAdded ? `${addedLabel} ✓` : addLabel;
      this.upsellModalAdd.classList.toggle('is-added', isAdded);
      this.upsellModalAdd.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
    }

    openDocumentDialog(link) {
      const href = String(link?.href || '').trim();
      if (!href) return;

      if (!this.documentDialog || typeof this.documentDialog.showModal !== 'function') {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }

      const title = String(link.dataset.documentTitle || link.textContent || 'Product Document')
        .replace(/[→↗]/g, '')
        .trim() || 'Product Document';
      const documentType = String(link.dataset.documentType || '').toLowerCase();
      const cleanUrl = href.split('#')[0].split('?')[0].toLowerCase();
      const isImage = documentType.includes('image') || /\.(png|jpe?g|gif|webp|avif|svg)$/.test(cleanUrl);

      this.documentTrigger = link;
      if (this.documentTitle) this.documentTitle.textContent = title;
      if (this.documentOpen) this.documentOpen.href = href;

      if (this.documentImage) {
        this.documentImage.hidden = !isImage;
        this.documentImage.alt = isImage ? title : '';
        if (isImage) this.documentImage.src = href;
        else this.documentImage.removeAttribute('src');
      }

      if (this.documentFrame) {
        this.documentFrame.hidden = isImage;
        this.documentFrame.title = title;
        this.documentFrame.src = isImage ? 'about:blank' : href;
      }

      document.documentElement.classList.add('product-v3-document-open');
      document.body?.classList.add('product-v3-document-open');
      this.documentDialog.showModal();
      this.documentClose?.focus();
    }

    closeDocumentDialog() {
      if (this.documentDialog?.open) this.documentDialog.close();
    }

    resetDocumentDialog() {
      document.documentElement.classList.remove('product-v3-document-open');
      document.body?.classList.remove('product-v3-document-open');

      if (this.documentImage) {
        this.documentImage.removeAttribute('src');
        this.documentImage.alt = '';
        this.documentImage.hidden = true;
      }

      if (this.documentFrame) {
        this.documentFrame.src = 'about:blank';
        this.documentFrame.hidden = true;
      }

      const trigger = this.documentTrigger;
      this.documentTrigger = null;
      if (trigger && document.contains(trigger)) trigger.focus();
    }

    async openMeasureDialog() {
      if (!this.measureDialog) return;
      const hasSavedLocation = Boolean(
        Number(sessionStorage.getItem('ls-lat') || 0) &&
        Number(sessionStorage.getItem('ls-lng') || 0)
      );
      this.measureAddressExpanded = !hasSavedLocation;
      document.documentElement.classList.add('product-v3-measure-open');
      document.body?.classList.add('product-v3-measure-open');
      this.measureDialog.showModal();
      try {
        await this.ensureMeasurementMap();
        this.startMeasurementSyncLoop();
        this.renderMeasureControls();
        this.refreshMeasurementMapLayout();
        if (this.measureAddressExpanded) this.measureAddressInput?.focus();
      } catch (error) {
        this.showMeasureError('The map could not load. Please close this window and try again.');
        console.warn('[Product V3] Measurement map failed to initialize', error);
      }
    }

    closeMeasureDialog() {
      this.cancelDraftMeasurement();
      this.stopMeasurementSyncLoop();
      this.measureDialog?.close();
    }

    waitForGoogleMaps(timeoutMs = 12000) {
      const mapsReady = () => (
        window.google?.maps?.Map
        && window.google?.maps?.geometry?.spherical
        && window.google?.maps?.places?.AutocompleteService
        && window.google?.maps?.places?.PlacesService
      );

      if (mapsReady()) return Promise.resolve();

      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      const mapsSrc = String(window.lsGoogleMapsSrc || '').trim();
      if (!existingScript && mapsSrc) {
        const script = document.createElement('script');
        script.src = mapsSrc;
        script.async = true;
        script.defer = true;
        script.dataset.lsGoogleMaps = 'true';
        document.head.appendChild(script);
      }

      return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        const timer = window.setInterval(() => {
          if (mapsReady()) {
            window.clearInterval(timer);
            resolve();
            return;
          }
          if (Date.now() - startedAt >= timeoutMs) {
            window.clearInterval(timer);
            reject(new Error('Google Maps libraries unavailable'));
          }
        }, 100);
      });
    }

    async ensureMeasurementMap() {
      if (this.measureMap) return;
      if (this.measureReadyPromise) return this.measureReadyPromise;

      this.measureReadyPromise = (async () => {
        await this.waitForGoogleMaps();
        if (!this.measureMapElement || !this.measureAddressInput) return;

        const savedLat = Number(sessionStorage.getItem('ls-lat') || 0);
        const savedLng = Number(sessionStorage.getItem('ls-lng') || 0);
        const hasSavedLocation = Number.isFinite(savedLat) && Number.isFinite(savedLng) && savedLat && savedLng;
        const center = hasSavedLocation ? { lat: savedLat, lng: savedLng } : { lat: 39.8283, lng: -98.5795 };

        this.measureMap = new google.maps.Map(this.measureMapElement, {
          center,
          zoom: hasSavedLocation ? 20 : 4,
          mapTypeId: 'satellite',
          tilt: 0,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER,
          },
          clickableIcons: false,
        });

        this.measureProjectionOverlay = new google.maps.OverlayView();
        this.measureProjectionOverlay.onAdd = () => {};
        this.measureProjectionOverlay.draw = () => {};
        this.measureProjectionOverlay.onRemove = () => {};
        this.measureProjectionOverlay.setMap(this.measureMap);

        this.measureAutocompleteService = new google.maps.places.AutocompleteService();
        this.measurePlacesService = new google.maps.places.PlacesService(this.measureMap);

        const savedAddress = sessionStorage.getItem('ls-address');
        if (savedAddress) this.measureAddressInput.value = savedAddress;

        if (hasSavedLocation) {
          this.measureStart.disabled = false;
          if (this.measureHelp) this.measureHelp.textContent = 'Choose Start measuring, then tap the corners of your lawn.';
        }

        this.renderMeasureControls();
      })();

      try {
        await this.measureReadyPromise;
      } finally {
        this.measureReadyPromise = null;
      }
    }

    async ensureMeasureAutocomplete() {
      if (this.measureAutocompleteService && this.measurePlacesService) return;
      await this.ensureMeasurementMap();

      if (!this.measureAutocompleteService) {
        this.measureAutocompleteService = new google.maps.places.AutocompleteService();
      }
      if (!this.measurePlacesService) {
        this.measurePlacesService = new google.maps.places.PlacesService(this.measureMap);
      }
    }

    scheduleMeasureSuggestions(value) {
      window.clearTimeout(this.measureSuggestionTimer);
      const input = String(value || '').trim();

      if (input.length < 3) {
        this.measureSuggestionItems = [];
        this.hideMeasureSuggestions();
        return;
      }

      this.measureSuggestionTimer = window.setTimeout(() => {
        this.fetchMeasureSuggestions(input);
      }, 220);
    }

    async fetchMeasureSuggestions(input) {
      const requestId = ++this.measureSuggestionRequestId;

      try {
        await this.ensureMeasureAutocomplete();

        const predictions = await new Promise((resolve, reject) => {
          this.measureAutocompleteService.getPlacePredictions(
            {
              input,
              componentRestrictions: { country: 'us' },
              types: ['address'],
            },
            (results, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
                resolve(results);
                return;
              }

              if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                resolve([]);
                return;
              }

              reject(new Error('Places autocomplete failed: ' + status));
            },
          );
        });

        if (requestId !== this.measureSuggestionRequestId) return;

        this.measureSuggestionItems = predictions.slice(0, 5);
        this.renderMeasureSuggestions();
      } catch (error) {
        if (requestId !== this.measureSuggestionRequestId) return;
        this.measureSuggestionItems = [];
        this.hideMeasureSuggestions();
        console.warn('[Product V3] Address suggestions unavailable', error);
      }
    }

    renderMeasureSuggestions() {
      if (!this.measureSuggestions || !this.measureAddressInput) return;

      this.measureSuggestions.replaceChildren();

      if (!this.measureSuggestionItems.length) {
        this.hideMeasureSuggestions();
        return;
      }

      this.measureSuggestionItems.forEach((prediction, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-v3__measure-suggestion';
        button.dataset.v3MeasureSuggestion = String(index);
        button.setAttribute('role', 'option');
        button.textContent = prediction.description;
        this.measureSuggestions.appendChild(button);
      });

      this.measureSuggestions.hidden = false;
      this.measureAddressInput.setAttribute('aria-expanded', 'true');
    }

    hideMeasureSuggestions() {
      if (this.measureSuggestions) this.measureSuggestions.hidden = true;
      this.measureAddressInput?.setAttribute('aria-expanded', 'false');
    }

    async selectMeasureSuggestion(index) {
      const prediction = this.measureSuggestionItems[index];
      if (!prediction?.place_id) return;

      this.hideMeasureSuggestions();
      this.hideMeasureError();

      try {
        await this.ensureMeasureAutocomplete();

        const place = await new Promise((resolve, reject) => {
          this.measurePlacesService.getDetails(
            {
              placeId: prediction.place_id,
              fields: ['geometry', 'formatted_address'],
            },
            (result, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && result) {
                resolve(result);
                return;
              }
              reject(new Error('Places details failed: ' + status));
            },
          );
        });

        const location = place.geometry?.location;
        if (!location) throw new Error('Missing place location');

        const lat = location.lat();
        const lng = location.lng();
        const formattedAddress = place.formatted_address || prediction.description;

        sessionStorage.setItem('ls-lat', String(lat));
        sessionStorage.setItem('ls-lng', String(lng));
        sessionStorage.setItem('ls-address', formattedAddress);

        if (this.measureAddressInput) this.measureAddressInput.value = formattedAddress;

        this.measureAddressExpanded = false;
        this.clearMeasurement();
        this.measureMap.setCenter({ lat, lng });
        this.measureMap.setZoom(20);

        if (this.measureStart) this.measureStart.disabled = false;
        if (this.measureHelp) this.measureHelp.textContent = 'Property found. Choose Start measuring, then tap the corners of your lawn.';
        this.renderMeasureControls();
        this.refreshMeasurementMapLayout({ lat, lng });

        this.measureSuggestionItems = [];
      } catch (error) {
        this.showMeasureError('We could not open that address. Please choose another result.');
        console.warn('[Product V3] Address suggestion selection failed', error);
      }
    }
    startMeasuring() {
      if (!this.measureMap || this.measureMode === 'drawing') return;

      this.hideMeasureError();
      this.cancelDraftMeasurement(false);
      this.measureMode = 'drawing';
      this.measureDraftPoints = [];

      this.measureDraftLine = new google.maps.Polyline({
        map: this.measureMap,
        path: [],
        strokeColor: '#e0a800',
        strokeOpacity: 1,
        strokeWeight: 3,
        clickable: false,
        zIndex: 2,
      });

      this.measureGuideLine = new google.maps.Polyline({
        map: this.measureMap,
        path: [],
        strokeColor: '#d93025',
        strokeOpacity: 1,
        strokeWeight: 2,
        clickable: false,
        zIndex: 3,
      });

      this.measureMoveListener?.remove();
      this.measureMoveListener = this.measureMap.addListener('mousemove', event => {
        if (this.measureMode !== 'drawing' || !event.latLng || !this.measureDraftPoints.length) return;
        this.updateMeasureGuide(event.latLng);
      });

      if (this.measureMapElement && this.measureProjectionOverlay) {
        this.measurePointerMoveHandler = event => {
          if (this.measureMode !== 'drawing' || !this.measureDraftPoints.length) return;
          const projection = this.measureProjectionOverlay?.getProjection?.();
          if (!projection) return;
          const rect = this.measureMapElement.getBoundingClientRect();
          const point = new google.maps.Point(event.clientX - rect.left, event.clientY - rect.top);
          const latLng = projection.fromContainerPixelToLatLng(point);
          if (latLng) this.updateMeasureGuide(latLng);
        };
        this.measureMapElement.addEventListener('pointermove', this.measurePointerMoveHandler, { passive: true });
      }

      this.measureClickListener?.remove();
      this.measureClickListener = this.measureMap.addListener('click', event => {
        if (!event.latLng || this.measureMode !== 'drawing') return;

        this.measureDraftPoints.push(event.latLng);
        this.measureDraftLine.setPath(this.measureDraftPoints);
        this.renderMeasureDraftMarkers();
        this.updateMeasureGuide();
        this.hideMeasureError();
        this.updateDraftMeasurement();
        this.renderMeasureControls();

        const remaining = Math.max(0, 3 - this.measureDraftPoints.length);
        if (this.measureHelp) {
          this.measureHelp.textContent = remaining
            ? `Tap ${remaining} more corner${remaining === 1 ? '' : 's'}, then choose Finish area.`
            : 'Keep tapping around the lawn edge, then choose Finish area.';
        }
      });

      this.measureMap.setOptions({ draggableCursor: 'crosshair' });
      this.renderMeasureControls();
      this.focusMeasurementMapOnMobile();

      if (this.measureHelp) {
        this.measureHelp.textContent = 'Tap the corners around the edge of the lawn, then choose Finish area.';
      }
    }

    finishMeasurementArea() {
      if (this.measureMode !== 'drawing' || !this.measureDraftLine) return;

      const draftPath = this.measureDraftLine.getPath();
      if (!draftPath || draftPath.getLength() < 3) {
        this.showMeasureError('Tap at least 3 corners on the lawn before finishing.');
        return;
      }

      const completedPath = [];
      draftPath.forEach(point => {
        completedPath.push({ lat: point.lat(), lng: point.lng() });
      });

      this.clearMeasureGuide();
      this.measureClickListener?.remove();
      this.measureClickListener = null;
      this.measureMap?.setOptions({ draggableCursor: null });

      this.measureDraftLine.setMap(null);
      this.measureDraftLine = null;
      this.clearMeasureDraftMarkers();
      this.measureDraftPoints = [];
      this.measureMode = 'idle';

      const polygon = new google.maps.Polygon({
        map: this.measureMap,
        paths: completedPath,
        fillColor: '#ffd23f',
        fillOpacity: 0.28,
        strokeColor: '#e0a800',
        strokeOpacity: 1,
        strokeWeight: 3,
        clickable: true,
        editable: true,
        draggable: true,
        zIndex: 2,
      });

      const area = {
        id: ++this.measureAreaId,
        polygon,
        marker: null,
      };

      this.measureAreas.push(area);
      this.bindMeasuredArea(area);
      this.recalculateMeasurement();
      this.renderMeasureControls();

      if (this.measureHelp) {
        this.measureHelp.textContent = 'Adjust the yellow points if needed, add another area, or tap the trash icon on an area to delete it.';
      }
    }

    cancelDraftMeasurement(render = true) {
      this.measureClickListener?.remove();
      this.measureClickListener = null;
      this.clearMeasureGuide();
      this.measureMap?.setOptions({ draggableCursor: null });

      this.measureDraftLine?.setMap(null);
      this.measureDraftLine = null;
      this.clearMeasureDraftMarkers();
      this.measureDraftPoints = [];
      this.measureMode = 'idle';

      if (render) {
        this.recalculateMeasurement();
        this.renderMeasureControls();
      }
    }

    clearMeasurement() {
      this.cancelDraftMeasurement(false);

      this.measureAreas.forEach(area => {
        area.polygon?.setMap(null);
        area.marker?.setMap(null);
      });

      this.measureAreas = [];
      this.pendingMeasuredSqft = 0;
      this.lastRenderedMeasuredSqft = null;
      sessionStorage.removeItem('ls-yard-area');

      this.updateMeasuredSize();
      this.renderMeasureControls();

      if (this.measureHelp) {
        this.measureHelp.textContent = 'Choose Start measuring, then tap the corners of your lawn.';
      }
    }

    undoMeasurePoint() {
      if (this.measureMode !== 'drawing' || !this.measureDraftLine) return;

      const path = this.measureDraftLine.getPath();
      if (!path?.getLength()) return;

      path.pop();
      this.measureDraftPoints = path.getArray();
      this.renderMeasureDraftMarkers();
      this.updateMeasureGuide();
      this.updateDraftMeasurement();
      this.renderMeasureControls();

      if (this.measureHelp) {
        this.measureHelp.textContent = this.measureDraftPoints.length >= 3
          ? 'Keep tapping around the lawn edge, then choose Finish area.'
          : 'Tap at least 3 corners to create an area.';
      }
    }

    renderMeasureControls() {
      const hasLocation = Boolean(
        Number(sessionStorage.getItem('ls-lat') || 0) &&
        Number(sessionStorage.getItem('ls-lng') || 0)
      );
      const hasCompletedAreas = this.measureAreas.length > 0;
      const drawing = this.measureMode === 'drawing';
      const state = drawing
        ? 'drawing'
        : hasCompletedAreas
          ? 'complete'
          : hasLocation
            ? 'ready'
            : 'address';

      if (this.measureShell) {
        this.measureShell.dataset.state = state;
        this.measureShell.dataset.address = hasLocation && !this.measureAddressExpanded ? 'collapsed' : 'expanded';
      }

      if (this.measureStart) {
        this.measureStart.hidden = false;
        this.measureStart.disabled = !hasLocation;
        this.measureStart.textContent = drawing
          ? 'Finish area'
          : hasCompletedAreas
            ? 'Add another area'
            : 'Start measuring';
      }

      if (this.measureUndo) {
        const draftLength = this.measureDraftLine?.getPath()?.getLength?.() || 0;
        this.measureUndo.hidden = !drawing;
        this.measureUndo.disabled = draftLength < 1;
      }

      if (this.measureClear) {
        const draftLength = this.measureDraftLine?.getPath()?.getLength?.() || 0;
        this.measureClear.disabled = !draftLength && !hasCompletedAreas;
      }

      if (this.measureChangeAddress) {
        this.measureChangeAddress.hidden = !hasLocation || this.measureAddressExpanded || drawing;
      }
    }

    refreshMeasurementMapLayout(center = null) {
      if (!this.measureMap || !window.google?.maps?.event) return;

      const targetCenter = center || this.measureMap.getCenter();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          google.maps.event.trigger(this.measureMap, 'resize');
          if (targetCenter) this.measureMap.setCenter(targetCenter);
        });
      });
    }

    focusMeasurementMapOnMobile() {
      if (!window.matchMedia?.('(max-width: 900px)').matches || !this.measureMapElement) return;

      window.setTimeout(() => {
        this.measureMapElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }

    startMeasurementSyncLoop() {
      this.stopMeasurementSyncLoop();

      this.measureSyncTimer = window.setInterval(() => {
        if (!this.measureAreas.length) return;
        this.recalculateMeasurement();
      }, 150);
    }

    stopMeasurementSyncLoop() {
      if (this.measureSyncTimer) {
        window.clearInterval(this.measureSyncTimer);
        this.measureSyncTimer = null;
      }
    }

    clearMeasureGuide() {
      this.measureMoveListener?.remove();
      this.measureMoveListener = null;

      if (this.measurePointerMoveHandler && this.measureMapElement) {
        this.measureMapElement.removeEventListener('pointermove', this.measurePointerMoveHandler);
      }

      this.measurePointerMoveHandler = null;
      this.measureGuideLine?.setMap(null);
      this.measureGuideLine = null;
    }

    clearMeasureDraftMarkers() {
      this.measureDraftMarkers.forEach(marker => marker.setMap(null));
      this.measureDraftMarkers = [];
    }

    renderMeasureDraftMarkers() {
      this.clearMeasureDraftMarkers();
      if (!this.measureMap || !this.measureDraftPoints.length) return;

      this.measureDraftPoints.forEach((point, index) => {
        const vertex = new google.maps.Marker({
          map: this.measureMap,
          position: point,
          clickable: false,
          zIndex: 5,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#ffd23f',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeOpacity: 1,
            strokeWeight: 1.5,
          },
        });
        this.measureDraftMarkers.push(vertex);

        const next = this.measureDraftPoints[index + 1];
        if (!next) return;

        const midpoint = new google.maps.LatLng(
          (point.lat() + next.lat()) / 2,
          (point.lng() + next.lng()) / 2,
        );
        const midpointMarker = new google.maps.Marker({
          map: this.measureMap,
          position: midpoint,
          clickable: false,
          zIndex: 4,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 3.5,
            fillColor: '#ffe27a',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeOpacity: 0.9,
            strokeWeight: 1,
          },
        });
        this.measureDraftMarkers.push(midpointMarker);
      });
    }

    updateMeasureGuide(cursorLatLng = null) {
      if (!this.measureGuideLine || !this.measureDraftPoints.length) return;

      const lastPoint = this.measureDraftPoints[this.measureDraftPoints.length - 1];
      if (!cursorLatLng) {
        this.measureGuideLine.setPath([]);
        return;
      }

      this.measureGuideLine.setPath([lastPoint, cursorLatLng]);
    }

    polygonSquareFeet(polygon) {
      if (!polygon || !window.google?.maps?.geometry?.spherical) return 0;
      const squareMeters = google.maps.geometry.spherical.computeArea(polygon.getPath());
      return Math.max(0, squareMeters * 10.7639);
    }

    polygonLabelPosition(polygon) {
      const path = polygon.getPath();
      if (!path?.getLength()) return this.measureMap.getCenter();

      let lat = 0;
      let lng = 0;
      path.forEach(point => {
        lat += point.lat();
        lng += point.lng();
      });

      const average = new google.maps.LatLng(lat / path.getLength(), lng / path.getLength());
      if (google.maps.geometry?.poly?.containsLocation?.(average, polygon)) return average;

      const bounds = new google.maps.LatLngBounds();
      path.forEach(point => bounds.extend(point));
      const center = bounds.getCenter();
      if (google.maps.geometry?.poly?.containsLocation?.(center, polygon)) return center;

      return path.getAt(0);
    }

    bindMeasuredArea(area) {
      if (!area?.polygon) return;

      const refresh = () => {
        this.updateMeasuredArea(area);
        this.recalculateMeasurement();
      };

      const path = area.polygon.getPath();
      path.addListener('set_at', refresh);
      path.addListener('insert_at', refresh);
      path.addListener('remove_at', refresh);
      area.polygon.addListener('drag', refresh);
      area.polygon.addListener('dragend', refresh);
      area.polygon.addListener('mouseup', refresh);
    }

    deleteMeasuredArea(areaId) {
      const index = this.measureAreas.findIndex(area => area.id === areaId);
      if (index < 0) return;

      const [area] = this.measureAreas.splice(index, 1);
      area.polygon?.setMap(null);
      area.marker?.setMap(null);

      this.recalculateMeasurement();
      this.renderMeasureControls();

      if (this.measureHelp) {
        this.measureHelp.textContent = this.measureAreas.length
          ? 'Adjust the remaining area, add another area, or tap its trash icon to delete it.'
          : 'Choose Start measuring, then tap the corners of your lawn.';
      }
    }

    measureAreaLabelIcon(text) {
      const safeText = String(text).replace(/[&<>"']/g, char => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&apos;',
      }[char]));

      const width = Math.max(132, Math.min(182, 68 + (safeText.length * 7)));
      const height = 38;
      const trashX = width - 24;

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect x="1" y="1" width="${width - 2}" height="${height - 2}" rx="19" fill="white" fill-opacity=".96" stroke="#d6ddd8"/><text x="${(width - 42) / 2}" y="52%" dominant-baseline="middle" text-anchor="middle" font-family="Arial,sans-serif" font-size="14" font-weight="700" fill="#17211b">${safeText}</text><g transform="translate(${trashX} 11)" fill="none" stroke="#9b2c22" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h12"/><path d="M5 3V1h6v2"/><path d="M4 5l1 10h6l1-10"/><path d="M7 7v5M10 7v5"/></g></svg>`;

      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(width, height),
        anchor: new google.maps.Point(width / 2, height / 2),
      };
    }

    createMeasureAreaMarker(area, position, text) {
      const marker = new google.maps.Marker({
        map: this.measureMap,
        position,
        clickable: true,
        optimized: false,
        zIndex: 999,
        cursor: 'pointer',
        title: 'Remove this measured area',
        icon: this.measureAreaLabelIcon(text),
      });

      marker.addListener('click', () => this.deleteMeasuredArea(area.id));
      return marker;
    }

    updateMeasuredArea(area) {
      if (!area?.polygon) return 0;

      const sqft = Math.round(this.polygonSquareFeet(area.polygon));
      const labelText = `${sqft.toLocaleString()} sq ft`;
      const position = this.polygonLabelPosition(area.polygon);

      if (!area.marker) {
        area.marker = this.createMeasureAreaMarker(area, position, labelText);
      } else {
        area.marker.setPosition(position);
        area.marker.setIcon(this.measureAreaLabelIcon(labelText));
      }

      return sqft;
    }

    syncMeasureAreas() {
      let totalSqft = 0;

      this.measureAreas.forEach(area => {
        totalSqft += this.updateMeasuredArea(area);
      });

      return totalSqft;
    }

    updateDraftMeasurement() {
      const completedSqft = this.syncMeasureAreas();

      this.pendingMeasuredSqft = Math.max(0, Math.round(completedSqft));
      sessionStorage.setItem('ls-yard-area', String(this.pendingMeasuredSqft));
      this.lastRenderedMeasuredSqft = this.pendingMeasuredSqft;
      this.updateMeasuredSize();
    }

    recalculateMeasurement() {
      const totalSqft = this.syncMeasureAreas();
      this.pendingMeasuredSqft = Math.max(0, Math.round(totalSqft));
      this.lastRenderedMeasuredSqft = this.pendingMeasuredSqft;
      sessionStorage.setItem('ls-yard-area', String(this.pendingMeasuredSqft));
      this.updateMeasuredSize();
    }

    updateMeasuredSize() {
      if (this.measuredSizeLabel) {
        this.measuredSizeLabel.textContent = `${this.pendingMeasuredSqft.toLocaleString()} sq ft`;
      }

      if (this.useMeasuredSize) {
        this.useMeasuredSize.disabled = !this.pendingMeasuredSqft;
      }

      this.renderMeasureControls();
    }

    showMeasureError(message) {
      if (!this.measureError) return;
      this.measureError.textContent = message;
      this.measureError.hidden = false;
    }

    hideMeasureError() {
      if (this.measureError) this.measureError.hidden = true;
    }

    applyMeasuredSize() {
      if (this.measureMode === 'drawing') {
        const path = this.measureDraftLine?.getPath();
        if (path && path.getLength() >= 3) {
          this.finishMeasurementArea();
        }
      }

      const sqft = Number(this.pendingMeasuredSqft || 0);
      if (!sqft) {
        this.showMeasureError('Measure at least one lawn area before using this size.');
        return;
      }

      if (this.measureOnly) {
        this.queueLawnSizeSave(sqft);
        this.measureTriggers.forEach(trigger => {
          trigger.textContent = `Measured: ${sqft.toLocaleString()} sq ft`;
        });
        window.dispatchEvent(new CustomEvent('ls:lawn-measured', {
          detail: {
            squareFeet: sqft,
            source: 'product-v3',
            productId: this.root.dataset.productId,
          },
        }));
        this.closeMeasureDialog();
        return;
      }

      if (this.input) this.input.value = sqft;
      if (this.slider) this.slider.value = Math.min(sqft, Number(this.slider.max || 30000));
      this.renderSqftValue(sqft);
      this.queueLawnSizeSave(sqft);

      this.hasInteracted = true;
      this.recommend(sqft);
      this.updateMixingGuide(sqft);
      this.closeMeasureDialog();
    }

    syncVariantGallery(variantId, animate = true) {
      const id = String(variantId || '');
      if (!id || id === this.galleryVariantId) return;
      this.galleryVariantId = id;
      const variant = this.variants.find(item => item.id === id);
      const mediaId = String(variant?.featuredMediaId || '');
      const index = this.galleryMediaIndexById.get(mediaId);
      if (index === undefined) return;
      this.scrollToSlide(index, animate);
    }

    scrollToSlide(index, animate = true) {
      const slide = this.slides[index];
      if (!this.gallery || !slide) return;
      this.gallery.scrollTo({ left: slide.offsetLeft, behavior: animate ? 'smooth' : 'instant' });
      this.setActiveThumb(index);
    }

    syncGalleryThumb() {
      if (!this.gallery || !this.slides.length) return;
      const left = this.gallery.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.setActiveThumb(closestIndex);
    }

    setActiveThumb(index) {
      this.activeGalleryIndex = index;
      this.thumbs.forEach((button, buttonIndex) => {
        button.setAttribute('aria-current', String(buttonIndex === index));
      });

      const active = this.thumbs[index];
      if (active && this.thumbViewport && window.innerWidth > 900) {
        const viewportTop = this.thumbViewport.scrollTop;
        const viewportBottom = viewportTop + this.thumbViewport.clientHeight;
        const activeTop = active.offsetTop;
        const activeBottom = activeTop + active.offsetHeight;
        let nextTop = viewportTop;

        if (activeTop < viewportTop) {
          nextTop = activeTop;
        } else if (activeBottom > viewportBottom) {
          nextTop = activeBottom - this.thumbViewport.clientHeight;
        }

        if (nextTop !== viewportTop) {
          const maxTop = Math.max(0, this.thumbViewport.scrollHeight - this.thumbViewport.clientHeight);
          this.thumbViewport.scrollTo({
            top: Math.max(0, Math.min(maxTop, nextTop)),
            behavior: 'smooth',
          });
        }
      }
      this.updateThumbNav();
      this.updateGalleryNav();
    }

    moveGallery(direction) {
      if (!this.slides.length) return;
      const current = Number.isInteger(this.activeGalleryIndex) ? this.activeGalleryIndex : 0;
      const next = Math.max(0, Math.min(this.slides.length - 1, current + direction));
      if (next === current) return;
      this.scrollToSlide(next);
    }

    updateGalleryNav() {
      if (!this.galleryPrev || !this.galleryNext) return;
      const hasMultiple = this.slides.length > 1;
      this.galleryPrev.hidden = !hasMultiple;
      this.galleryNext.hidden = !hasMultiple;
      if (!hasMultiple) return;

      const current = Number.isInteger(this.activeGalleryIndex) ? this.activeGalleryIndex : 0;
      this.galleryPrev.disabled = current <= 0;
      this.galleryNext.disabled = current >= this.slides.length - 1;
    }

    updateThumbNav() {
      if (!this.thumbViewport || !this.thumbPrev || !this.thumbNext || window.innerWidth <= 900) return;
      const max = Math.max(0, this.thumbViewport.scrollHeight - this.thumbViewport.clientHeight);
      this.thumbPrev.disabled = this.thumbViewport.scrollTop <= 2;
      this.thumbNext.disabled = this.thumbViewport.scrollTop >= max - 2;
      const needsScroll = max > 2;
      this.thumbPrev.hidden = !needsScroll;
      this.thumbNext.hidden = !needsScroll;
    }

    plannerSqftForVariant(variant) {
      const rate = this.rateForVariant(variant);
      if (!variant || !rate) return 0;

      const basisSqft = applicationBasisSqft(rate.basis);
      const amount = this.variantAmountForRate(variant, rate);
      const rateMax = Number(rate.max || 0);
      if (!basisSqft || !amount || !rateMax) return 0;

      const sliderMin = Math.max(1, Number(this.slider?.min || 1));
      const sliderMax = Math.max(sliderMin, Number(this.slider?.max || 30000));
      const sliderStep = Math.max(1, Number(this.slider?.step || 1));
      const minimumCoverageSqft = (amount / rateMax) * basisSqft;
      const stepCount = Math.ceil(Math.max(0, minimumCoverageSqft - sliderMin) / sliderStep);
      const alignedSqft = sliderMin + (stepCount * sliderStep);

      return Math.max(sliderMin, Math.min(sliderMax, alignedSqft));
    }

    renderIncomingVariant() {
      const variant = this.variants.find(item => item.id === this.selectedVariantId) || this.variants[0];
      if (!variant) return;

      if (this.hasIncomingVariantParam) {
        const incomingSqft = this.plannerSqftForVariant(variant);
        if (incomingSqft) {
          if (this.slider) this.slider.value = String(incomingSqft);
          if (this.input) this.input.value = String(incomingSqft);
        }

        const legacyMapping = this.legacyVariantMapping(variant.id);
        if (legacyMapping) {
          const mappedLines = legacyMapping.lines
            .map(mappedLine => ({
              variant: this.variants.find(item => item.id === mappedLine.variantId),
              quantity: mappedLine.quantity,
            }))
            .filter(line => line.variant && line.quantity > 0);

          if (mappedLines.length) {
            this.legacyIncomingSelection = {
              displayWeightLb: legacyMapping.displayWeightLb || variant.weightLb,
              priceCents: variant.priceCents,
              packageText: mappedLines
                .map(line => `${line.quantity} × ${this.formatWeight(line.variant.weightLb)}`)
                .join(' + '),
            };
            this.applyRecommendation({
              lines: mappedLines,
              totalWeight: mappedLines.reduce(
                (sum, line) => sum + (line.variant.weightLb * line.quantity),
                0,
              ),
              totalBags: mappedLines.reduce((sum, line) => sum + line.quantity, 0),
              validRange: true,
              incoming: true,
              incomingFromVariantUrl: true,
              legacyMapped: true,
              legacyDisplayWeightLb: legacyMapping.displayWeightLb || variant.weightLb,
            }, false);
            return;
          }
        }
      }

      this.applyRecommendation({
        variant,
        quantity: 1,
        totalWeight: variant.weightLb,
        validRange: true,
        incoming: true,
        incomingFromVariantUrl: this.hasIncomingVariantParam,
      }, false);
    }

    readMyLawn() {
      try {
        const raw = localStorage.getItem('ls-mylawn-v1');
        return raw ? JSON.parse(raw) : {};
      } catch (_) {
        return {};
      }
    }

    loadSavedLawn() {
      const saved = this.readMyLawn();
      const area = saved?.activeArea || saved?.pendingArea || saved?.areas?.[0];
      const sqft = Number(area?.squareFeet || 0);
      if (!sqft) return false;

      this.lastSavedSqft = Math.round(sqft);
      this.queueLawnSizeSave(this.lastSavedSqft, { writeLocal: false });
      if (this.hasIncomingVariantParam) return true;

      if (this.input) this.input.value = this.lastSavedSqft;
      if (this.slider) this.slider.value = Math.min(this.lastSavedSqft, Number(this.slider.max || 30000));
      this.renderSqftValue(this.lastSavedSqft);

      this.recommend(this.lastSavedSqft);
      this.updateMixingGuide(this.lastSavedSqft);
      return true;
    }

    saveMyLawn(sqft) {
      const value = Math.max(1, Math.round(Number(sqft || 0)));
      if (!value) return false;

      const saved = this.readMyLawn();
      if (!Array.isArray(saved.areas)) saved.areas = [];

      saved.pendingArea = {
        squareFeet: value,
        source: 'product-v3',
        productId: this.root.dataset.productId,
        updatedAt: new Date().toISOString(),
      };

      try {
        localStorage.setItem('ls-mylawn-v1', JSON.stringify(saved));
        sessionStorage.setItem('ls-yard-area', String(value));
      } catch (error) {
        console.warn('[Product V3] Unable to save lawn size locally', error);
        return false;
      }

      this.lastSavedSqft = value;
      return true;
    }

    queueLawnSizeSave(sqft, options = {}) {
      const value = Math.max(1, Math.round(Number(sqft || 0)));
      if (!value) return;

      this.lawnSizeTouched = true;
      this.pendingLawnSizeSave = value;
      if (options.writeLocal !== false) this.saveMyLawn(value);

      if (this.lawnSizeSaveTimer) window.clearTimeout(this.lawnSizeSaveTimer);
      this.lawnSizeSaveTimer = window.setTimeout(() => {
        this.lawnSizeSaveTimer = null;
        this.flushLawnSizeSave();
      }, 450);
    }

    async saveLawnSizeCartAttribute(sqft) {
      const value = Math.max(1, Math.round(Number(sqft || 0)));
      if (!value) return;

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          attributes: {
            '__ls_yard_area': String(value),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to save lawn size to cart.');
      }

      window.dispatchEvent(new CustomEvent('ls:mylawn:save-requested', {
        detail: {
          squareFeet: value,
          source: 'product-v3',
          productId: this.root.dataset.productId,
        },
      }));
    }

    async flushLawnSizeSave() {
      if (this.lawnSizeSaveTimer) {
        window.clearTimeout(this.lawnSizeSaveTimer);
        this.lawnSizeSaveTimer = null;
      }

      const value = Math.max(1, Math.round(Number(this.pendingLawnSizeSave || 0)));
      if (!this.lawnSizeTouched || !value) return;

      this.lawnSizeSavePromise = this.lawnSizeSavePromise
        .catch(() => {})
        .then(() => this.saveLawnSizeCartAttribute(value))
        .catch(error => {
          console.warn('[Product V3] Unable to save lawn size to cart', error);
        });

      await this.lawnSizeSavePromise;
    }

    recommend(sqft) {
      if (!this.variants.length || !sqft) return;

      const rate = this.formatConfig ? this.rateForVariant(this.formatVariants().find(v=>v.available)) : this.activeApplicationRate();
      const basisSqft = applicationBasisSqft(rate?.basis);
      if (!rate || !basisSqft) return;

      const minAmount = (sqft / basisSqft) * Number(rate.min || 0);
      const maxAmount = (sqft / basisSqft) * Number(rate.max || 0);
      const unit = normalizeApplicationUnit(rate.unit || 'lb');

      let recommendation = null;
      if (unit === 'lb') {
        recommendation = this.pickSimpleRecommendation(minAmount, maxAmount);
        if (recommendation) {
          recommendation.minLb = minAmount;
          recommendation.maxLb = maxAmount;
          recommendation.targetLb = minAmount;
        }
      } else {
        recommendation = this.pickApplicationRecommendation(
          this.plannerVariants,
          minAmount,
          maxAmount,
          rate,
        );
        if (recommendation) {
          recommendation.minAmount = minAmount;
          recommendation.maxAmount = maxAmount;
          recommendation.needUnit = rate.unit;
        }
      }

      if (!recommendation) return;

      this.renderSqftValue(sqft);
      recommendation.sqft = sqft;
      recommendation.targetRate = Number(rate.min || 0);
      this.applyRecommendation(recommendation);
      this.updateUpsellRecommendation(sqft);
    }

    pickSimpleRecommendation(minLb, maxLb) {
      let variants = this.formatConfig ? this.plannerVariants.filter(v=>this.formatForVariant(v)===this.selectedFormat) : this.plannerVariants;
      const has25Lb = variants.some(variant => Math.abs(Number(variant.weightLb || 0) - 25) < 0.01);
      const has50Lb = variants.some(variant => Math.abs(Number(variant.weightLb || 0) - 50) < 0.01);

      if (has25Lb && has50Lb) {
        if (minLb <= 50) {
          const singlePackage = variants
            .filter(variant => {
              const weight = Number(variant.weightLb || 0);
              return weight > 0 && weight <= 50.01 && weight + 0.01 >= minLb;
            })
            .sort((a, b) => {
              if (a.weightLb !== b.weightLb) return a.weightLb - b.weightLb;
              return a.priceCents - b.priceCents;
            })[0];

          if (singlePackage) {
            const lines = [{ variant: singlePackage, quantity: 1 }];
            const pricing = this.calculateLinePricing(lines);
            return {
              lines,
              totalWeight: singlePackage.weightLb,
              totalPriceCents: pricing.finalPriceCents,
              listPriceCents: pricing.listPriceCents,
              discountCents: pricing.discountCents,
              totalBags: 1,
              validRange: singlePackage.weightLb <= maxLb,
              excess: Math.max(0, singlePackage.weightLb - maxLb),
            };
          }
        }

        if (minLb > 50) {
          variants = variants.filter(variant => {
            const weight = Number(variant.weightLb || 0);
            const increments = weight / 25;
            return weight >= 25 && Math.abs(increments - Math.round(increments)) < 0.001;
          });
        }
      }

      return this.pickSimpleRecommendationForVariants(variants, minLb, maxLb, true);
    }

    pickApplicationRecommendation(variants, minAmount, maxAmount, rate) {
      if (!variants?.length || !rate) return null;

      const eligible = variants
        .map(variant => ({
          variant,
          amount: this.variantAmountForRate(variant, rate),
        }))
        .filter(item => item.amount > 0);
      if (!eligible.length) return null;

      const candidates = [];
      const maxVariantAmount = Math.max(...eligible.map(item => item.amount));
      const maxCandidateAmount = maxAmount + maxVariantAmount;

      const buildCandidates = (index, lines, totalAmount, totalPriceCents, totalItems) => {
        if (index === eligible.length) {
          if (!totalItems || totalAmount < minAmount) return;

          const candidateLines = lines.filter(line => line.quantity > 0);
          const pricing = this.calculateLinePricing(candidateLines);
          const validRange = totalAmount <= maxAmount;
          candidates.push({
            lines: candidateLines,
            totalMeasure: totalAmount,
            totalPriceCents: pricing.finalPriceCents,
            listPriceCents: pricing.listPriceCents,
            discountCents: pricing.discountCents,
            totalBags: totalItems,
            validRange,
            excess: Math.max(0, totalAmount - maxAmount),
          });
          return;
        }

        const item = eligible[index];
        const remainingAmount = Math.max(0, maxCandidateAmount - totalAmount);
        const maxQty = Math.ceil(remainingAmount / item.amount);

        for (let quantity = 0; quantity <= maxQty; quantity += 1) {
          const nextAmount = totalAmount + (item.amount * quantity);
          if (nextAmount > maxCandidateAmount) break;

          const nextLines = quantity > 0
            ? [...lines, { variant: item.variant, quantity }]
            : lines;

          buildCandidates(
            index + 1,
            nextLines,
            nextAmount,
            totalPriceCents + (item.variant.priceCents * quantity),
            totalItems + quantity,
          );
        }
      };

      buildCandidates(0, [], 0, 0, 0);
      if (!candidates.length) return null;

      candidates.sort((a, b) => {
        if (a.totalPriceCents !== b.totalPriceCents) return a.totalPriceCents - b.totalPriceCents;
        if (a.validRange !== b.validRange) return a.validRange ? -1 : 1;
        if (a.totalBags !== b.totalBags) return a.totalBags - b.totalBags;
        if (a.excess !== b.excess) return a.excess - b.excess;
        if (a.totalMeasure !== b.totalMeasure) return a.totalMeasure - b.totalMeasure;
        return 0;
      });

      return candidates[0];
    }

    pickSimpleRecommendationForVariants(variants, minLb, maxLb, applyQuantityPricing = false) {
      if (!variants?.length) return null;

      const candidates = [];
      const maxVariantWeight = Math.max(...variants.map(variant => variant.weightLb));
      const maxCandidateWeight = maxLb + maxVariantWeight;

      const buildCandidates = (index, lines, totalWeight, totalPriceCents, totalBags) => {
        if (index === variants.length) {
          if (!totalBags || totalWeight < minLb) return;

          const candidateLines = lines.filter(line => line.quantity > 0);
          const pricing = applyQuantityPricing
            ? this.calculateLinePricing(candidateLines)
            : {
              listPriceCents: totalPriceCents,
              discountCents: 0,
              finalPriceCents: totalPriceCents,
            };
          const validRange = totalWeight <= maxLb;
          candidates.push({
            lines: candidateLines,
            totalWeight,
            totalPriceCents: pricing.finalPriceCents,
            listPriceCents: pricing.listPriceCents,
            discountCents: pricing.discountCents,
            totalBags,
            validRange,
            excess: Math.max(0, totalWeight - maxLb),
          });
          return;
        }

        const variant = variants[index];
        const remainingWeight = Math.max(0, maxCandidateWeight - totalWeight);
        const maxQty = Math.ceil(remainingWeight / variant.weightLb);

        for (let quantity = 0; quantity <= maxQty; quantity += 1) {
          const nextWeight = totalWeight + (variant.weightLb * quantity);
          if (nextWeight > maxCandidateWeight) break;

          const nextLines = quantity > 0
            ? [...lines, { variant, quantity }]
            : lines;

          buildCandidates(
            index + 1,
            nextLines,
            nextWeight,
            totalPriceCents + (variant.priceCents * quantity),
            totalBags + quantity,
          );
        }
      };

      buildCandidates(0, [], 0, 0, 0);
      if (!candidates.length) return null;

      candidates.sort((a, b) => {
        if (a.totalPriceCents !== b.totalPriceCents) return a.totalPriceCents - b.totalPriceCents;
        if (a.validRange !== b.validRange) return a.validRange ? -1 : 1;
        if (a.totalBags !== b.totalBags) return a.totalBags - b.totalBags;
        if (a.excess !== b.excess) return a.excess - b.excess;
        if (a.totalWeight !== b.totalWeight) return a.totalWeight - b.totalWeight;
        return 0;
      });

      return candidates[0];
    }

    updateUpsellRecommendation(sqft) {
      this.upsellRecommendation = null;
      if (!this.upsellData) {
        this.renderUpsellRecommendation();
        this.updateAtcLabel();
        return;
      }

      if (this.upsellData.usesWeightRate && sqft) {
        const weightedVariants = this.upsellData.variants.filter(variant => variant.weightLb > 0);
        const minLb = (sqft / 1000) * this.upsellData.rateMin;
        const maxLb = (sqft / 1000) * this.upsellData.rateMax;
        const recommendation = this.pickSimpleRecommendationForVariants(
          weightedVariants,
          minLb,
          maxLb,
        );

        if (recommendation) {
          this.upsellRecommendation = {
            ...recommendation,
            sqft,
            minLb,
            maxLb,
          };
        }
      } else {
        const variant = this.upsellData.variants[0];
        if (variant) {
          this.upsellRecommendation = {
            lines: [{ variant, quantity: 1 }],
            totalBags: 1,
            totalWeight: Number(variant.weightLb || 0),
            sqft: 0,
          };
        }
      }

      this.renderUpsellRecommendation();
      this.updateAtcLabel();
    }

    renderUpsellRecommendation() {
      if (!this.upsellSummary && !this.upsellPrice && !this.upsellModalSummary && !this.upsellModalPrice) return;

      const recommendation = this.upsellRecommendation;
      if (!recommendation?.lines?.length) {
        if (this.upsellSummary) this.upsellSummary.textContent = '';
        if (this.upsellPrice) this.upsellPrice.textContent = '';
        if (this.upsellModalSummary) this.upsellModalSummary.textContent = '';
        if (this.upsellModalPrice) this.upsellModalPrice.textContent = '';
        return;
      }

      const packageText = recommendation.lines
        .map(line => {
          const variantTitle = String(line.variant.title || '').trim();
          if (!variantTitle || variantTitle.toLowerCase() === 'default title') {
            // A single default variant has no customer-facing option to describe.
            return line.quantity > 1
              ? `${line.quantity} × ${this.upsellData?.title || 'item'}`
              : '';
          }
          return `${line.quantity} × ${variantTitle}`;
        })
        .filter(Boolean)
        .join(' + ');
      const coverageText = this.upsellData?.usesWeightRate && recommendation.sqft
        ? ` for ${Math.round(recommendation.sqft).toLocaleString()} sq ft`
        : '';
      const pricing = this.calculateLinePricing(recommendation.lines);
      const cardSummaryText = packageText;
      const modalSummaryText = `${packageText}${coverageText}`.trim();
      const priceText = `+ ${money(pricing.finalPriceCents, this.currency)}`;

      if (this.upsellSummary) this.upsellSummary.textContent = cardSummaryText;
      if (this.upsellPrice) this.upsellPrice.textContent = priceText;
      if (this.upsellModalSummary) this.upsellModalSummary.textContent = modalSummaryText;
      if (this.upsellModalPrice) this.upsellModalPrice.textContent = priceText;
    }

    upsellCartLines() {
      if (!this.upsellToggle?.checked || !this.upsellRecommendation?.lines?.length) return [];
      return this.upsellRecommendation.lines;
    }

    setAtcLabel(label, priceCents = 0) {
      if (!this.atcMain || !this.atcPrice || !this.atcSeparator) return;
      this.atcMain.textContent = label;
      const showPrice = priceCents > 0;
      this.atcPrice.textContent = showPrice ? money(priceCents, this.currency) : '';
      this.atcPrice.hidden = !showPrice;
      this.atcSeparator.hidden = !showPrice;
    }

    updateAtcLabel() {
      if (!this.atcMain) return;

      const primaryTotal = Number(this.currentRecommendation?.totalPriceCents || 0);
      const addUpsell = Boolean(this.upsellToggle?.checked && this.upsellRecommendation?.lines?.length);
      const upsellTotal = addUpsell
        ? Number(this.calculateLinePricing(this.upsellRecommendation.lines).finalPriceCents || 0)
        : 0;
      this.setAtcLabel(addUpsell ? 'Add Both to Cart' : this.baseAtcText, primaryTotal + upsellTotal);
    }

    syncReviewCountFromMainWidget() {
      if (!this.reviews || !this.reviewCountSync) return;

      const findMainReviewSection = () => (
        document.querySelector('[data-v3-main-reviews]')
        || document.querySelector('.section-ls-v3-reviews')
        || document.querySelector('[data-yotpo-instance-id="1224693"]')
      );

      const readMainReviewCount = section => {
        if (!section) return null;

        const text = String(section.innerText || section.textContent || '')
          .replace(/\s+/g, ' ')
          .trim();

        const basedOnMatch = text.match(/\bbased on\s+([\d,]+)\s+reviews?\b/i);
        if (basedOnMatch) return Number(basedOnMatch[1].replace(/,/g, ''));

        const countMatch = text.match(/\b([\d,]+)\s+reviews?\b/i);
        return countMatch ? Number(countMatch[1].replace(/,/g, '')) : null;
      };

      const syncCount = () => {
        const section = findMainReviewSection();
        const count = readMainReviewCount(section);
        if (!Number.isFinite(count)) return false;

        this.reviewCountSync.textContent = `${count.toLocaleString()} Reviews`;
        this.reviewCountSync.hidden = false;
        return true;
      };

      const scheduleSync = () => {
        if (this.reviewSyncFrame) cancelAnimationFrame(this.reviewSyncFrame);
        this.reviewSyncFrame = requestAnimationFrame(() => {
          this.reviewSyncFrame = null;
          syncCount();
        });
      };

      let setupAttempts = 0;
      const attachObserver = () => {
        const section = findMainReviewSection();

        if (!section) {
          if (setupAttempts < 80) {
            setupAttempts += 1;
            window.setTimeout(attachObserver, 250);
          }
          return;
        }

        this.reviewSyncObserver?.disconnect();
        this.reviewSyncObserver = new MutationObserver(scheduleSync);
        this.reviewSyncObserver.observe(section, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        scheduleSync();

        let countAttempts = 0;
        const waitForCount = () => {
          if (syncCount()) return;
          if (countAttempts < 80) {
            countAttempts += 1;
            window.setTimeout(waitForCount, 250);
          }
        };
        waitForCount();
      };

      attachObserver();
    }

    lineItemProperties() {
      const properties = {};
      const applicationType = String(this.applicationTypePropertyInput?.value || '').trim();
      const purchaseMethod = String(this.purchaseMethodPropertyInput?.value || '').trim();
      if (applicationType) properties['_Application Type'] = applicationType;
      if (purchaseMethod) properties['_Purchase Method'] = purchaseMethod;
      return Object.keys(properties).length ? properties : null;
    }

    async handleMixedSubmit(event) {
      const lines = this.currentRecommendation?.lines || [];
      const upsellLines = this.upsellCartLines();
      const requiresCustomAdd = lines.length > 1 || upsellLines.length > 0;

      if (!requiresCustomAdd) {
        if (!this.lawnSizeTouched || this.lawnSizeSubmitBypass) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        await this.flushLawnSizeSave();

        this.lawnSizeSubmitBypass = true;
        try {
          this.form?.requestSubmit(this.atc || undefined);
        } finally {
          this.lawnSizeSubmitBypass = false;
        }
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.atc || this.atc.disabled) return;

      this.atc.disabled = true;
      this.setAtcLabel('Adding…');
      if (this.formError) this.formError.textContent = '';

      try {
        await this.flushLawnSizeSave();

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = typeof cart?.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map(section => section.id)
          : [];

        const lineItemProperties = this.lineItemProperties();
        const items = lines.map(line => ({
          id: Number(String(line.variant.id).split('/').pop()),
          quantity: line.quantity,
          ...(lineItemProperties ? { properties: lineItemProperties } : {}),
        }));

        const purchaseMethod = String(this.purchaseMethodPropertyInput?.value || '').trim();
        const upsellProperties = {};
        if (purchaseMethod) upsellProperties['_Purchase Method'] = purchaseMethod;

        upsellLines.forEach(line => {
          items.push({
            id: Number(String(line.variant.id).split('/').pop()),
            quantity: line.quantity,
            ...(Object.keys(upsellProperties).length ? { properties: upsellProperties } : {}),
          });
        });

        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const parsedState = await response.json();
        if (!response.ok || parsedState?.status) {
          throw new Error(parsedState?.description || parsedState?.message || 'We could not add these items to your cart.');
        }

        if (cart && typeof cart.renderContents === 'function') {
          cart.setActiveElement?.(document.activeElement);
          cart.renderContents(parsedState);
        } else {
          window.location = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        if (this.formError) this.formError.textContent = error?.message || 'We could not add these items to your cart.';
      } finally {
        this.atc.disabled = false;
        this.updateAtcLabel();
      }
    }

    applyRecommendation(rec, updateUrl = true) {
      const lines = (rec.lines?.length ? rec.lines : [{ variant: rec.variant, quantity: rec.quantity || 1 }])
        .filter(line => line.variant && line.quantity > 0)
        .sort((a, b) => b.variant.weightLb - a.variant.weightLb);

      if (!lines.length) return;

      const totalBags = lines.reduce((sum, line) => sum + line.quantity, 0);
      const totalWeight = lines.reduce((sum, line) => sum + (line.variant.weightLb * line.quantity), 0);
      const pricing = this.calculateLinePricing(lines);
      const listPriceCents = pricing.listPriceCents;
      const discountCents = pricing.discountCents;
      const totalPriceCents = pricing.finalPriceCents;
      const primaryLine = lines[0];
      const singleLine = lines.length === 1;

      this.currentRecommendation = {
        ...rec,
        lines,
        totalBags,
        totalWeight,
        listPriceCents,
        discountCents,
        totalPriceCents,
      };
      this.selectedVariantId = primaryLine.variant.id;
      this.syncVariantGallery(this.selectedVariantId);

      if (this.variantIdInput) this.variantIdInput.value = primaryLine.variant.id;
      if (this.formatConfig) this.setApplicationTypeProperty(this.formatConfig[this.selectedFormat]?.label || '');
      if (this.quantityInput) this.quantityInput.value = singleLine ? primaryLine.quantity : 1;
      if (this.price) {
        const preserveLegacyLandingPrice = Boolean(this.legacyIncomingSelection && rec.quick);
        const showingBundlePrice = Boolean((rec.incomingFromVariantUrl && rec.legacyMapped) || preserveLegacyLandingPrice);
        const displayPriceCents = showingBundlePrice ? totalPriceCents : primaryLine.variant.priceCents;
        if (this.priceCurrent) this.priceCurrent.textContent = money(displayPriceCents, this.currency);
        if (this.priceCompare) {
          const compareAtPriceCents = primaryLine.variant.compareAtPriceCents || 0;
          const showCompareAtPrice = !showingBundlePrice && compareAtPriceCents > displayPriceCents;
          this.priceCompare.hidden = !showCompareAtPrice;
          if (showCompareAtPrice) this.priceCompare.textContent = money(compareAtPriceCents, this.currency);
        }
      }

      const rate = this.activeApplicationRate();
      const rateUnit = normalizeApplicationUnit(rate?.unit || 'lb');
      const liquidCalculation = rateUnit !== 'lb';
      const packageText = liquidCalculation
        ? lines
          .map(line => `${line.quantity} × ${String(line.variant.title || 'Size')}`)
          .join(' + ')
        : lines
          .map(line => `${line.quantity} × ${this.formatWeight(line.variant.weightLb)}`)
          .join(' + ');

      let recommendationContextText = '';
      if (rec.incomingFromVariantUrl && rec.legacyMapped) {
        recommendationContextText = `${this.formatWeight(rec.legacyDisplayWeightLb || totalWeight)} selected from your shopping link`;
      } else if (rec.incomingFromVariantUrl && singleLine) {
        recommendationContextText = rateUnit !== 'lb'
          ? `${String(primaryLine.variant.title || 'Size')} selected from your shopping link`
          : `${this.formatWeight(primaryLine.variant.weightLb)} selected from your shopping link`;
      } else if (rec.incoming && singleLine) {
        recommendationContextText = rateUnit !== 'lb'
          ? `Selected: ${String(primaryLine.variant.title || 'Size')}`
          : `Selected: ${this.formatWeight(primaryLine.variant.weightLb)}`;
      } else if (rec.minAmount && rec.maxAmount) {
        recommendationContextText = `${this.formatApplicationRange(rec.minAmount, rec.maxAmount, rec.needUnit || rate?.unit)} needed`;
      } else if (rec.minLb && rec.maxLb) {
        recommendationContextText = `${this.formatNeedRange(rec.minLb, rec.maxLb)} needed`;
      } else {
        recommendationContextText = rateUnit !== 'lb'
          ? `${String(primaryLine.variant.title || 'Size')} recommended`
          : `${this.formatWeight(totalWeight)} recommended`;
      }

      if (this.recommendationPackage) {
        this.recommendationPackage.dataset.discounted = String(discountCents > 0);
      }

      if (this.recommendationTitle) {
        this.recommendationTitle.textContent = recommendationContextText;
      }


      if (this.recommendationDetail) {
        this.recommendationDetail.textContent = packageText;
      }

      if (this.recommendationListTotal) {
        this.recommendationListTotal.textContent = money(listPriceCents, this.currency);
        this.recommendationListTotal.hidden = discountCents <= 0;
      }

      if (this.recommendationTotal) {
        this.recommendationTotal.textContent = money(totalPriceCents, this.currency);
      }

      if (this.recommendationSavings) {
        this.recommendationSavings.textContent = discountCents > 0
          ? `SAVE ${money(discountCents, this.currency)}`
          : '';
        this.recommendationSavings.hidden = discountCents <= 0;
      }

      if (this.recommendationNote) {
        if (rec.sqft && !rec.validRange && (rec.maxLb || rec.maxAmount)) {
          const needText = rec.maxAmount
            ? this.formatApplicationRange(rec.minAmount, rec.maxAmount, rec.needUnit || rate?.unit)
            : this.formatNeedRange(rec.minLb, rec.maxLb);
          this.recommendationNote.textContent = `Apply ${needText} now and save the remainder for your next application.`;
          this.recommendationNote.hidden = false;
        } else {
          this.recommendationNote.hidden = true;
        }
      }

      this.updateAtcLabel(totalBags);
      if (this.atc) this.atc.disabled = lines.some(line => !line.variant.available);

      if (this.bulk) {
        const isBulk = totalWeight >= this.wholesaleLb;
        this.bulk.dataset.prominent = String(isBulk);
        const bulkMessage = this.bulk.querySelector('[data-v3-bulk-message]');
        if (bulkMessage) bulkMessage.hidden = !isBulk;
      }

      if (updateUrl && this.hasInteracted && singleLine && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(primaryLine.variant.id).replace('gid://shopify/ProductVariant/', ''));
        window.history.replaceState({}, '', url.toString());
      }

      this.recommendation?.removeAttribute('hidden');
      window.dispatchEvent(new CustomEvent('ls:product-v3:recommendation', { detail: this.currentRecommendation }));
    }

    formatWeight(lb) {
      return `${this.trim(lb)} lb`;
    }

    formatNeedRange(minLb, maxLb) {
      const min = Number(minLb || 0);
      const max = Number(maxLb || 0);
      if (!min || !max) return '';
      if (Math.abs(max - min) < 0.05) return `${this.trim(min)} lb`;
      return `${this.trim(min)}–${this.trim(max)} lbs`;
    }

    formatApplicationRange(minValue, maxValue, unit) {
      const min = Number(minValue || 0);
      const max = Number(maxValue || 0);
      const label = String(unit || '').trim();
      if (!min || !max || !label) return '';
      if (Math.abs(max - min) < 0.05) return `${this.trim(min)} ${label}`;
      return `${this.trim(min)}–${this.trim(max)} ${label}`;
    }

    trim(value) {
      return Number(value.toFixed(1)).toLocaleString();
    }
  }

  // Shopify controls the Shop Promise widget and may mount its host before
  // rendering delivery details. Observe the actual content inside its open
  // shadow root so the trust strip never treats an empty host as a loaded widget.
  const alignShopPromiseSummary = root => {
    const container = root.querySelector('.product-v3__shop-promise--inside');
    if (!container || container.dataset.v3PromiseAlignment === 'true') return;
    container.dataset.v3PromiseAlignment = 'true';

    let watchedShadow = null;
    let shadowObserver = null;

    const syncPromise = () => {
      const host = container.querySelector('delivery-promise-wc');
      const shadow = host?.shadowRoot;

      if (shadow !== watchedShadow) {
        shadowObserver?.disconnect();
        watchedShadow = shadow || null;
        if (watchedShadow) {
          shadowObserver = new MutationObserver(syncPromise);
          shadowObserver.observe(watchedShadow, { childList: true, subtree: true });
        }
      }

      const rendered = Boolean(shadow?.querySelector('.ShopPromiseLayout__Clickable'));
      root.dataset.v3PromiseRendered = rendered ? 'true' : 'false';

      if (!shadow || shadow.querySelector('[data-v3-promise-alignment-style]')) return;
      const style = document.createElement('style');
      style.dataset.v3PromiseAlignmentStyle = 'true';
      style.textContent = `
        @media (min-width: 901px) {
          .ShopPromiseLayout__Clickable > .BlockStack__BlockStack > .InlineStack__InlineStack {
            justify-content: center !important;
          }
        }
      `;
      shadow.append(style);
    };

    // Observe only the Promise wrapper and its own shadow DOM. A delayed
    // Shopify render updates the spacing without page reloads or re-initializing
    // Shopify's external widget.
    const observer = new MutationObserver(syncPromise);
    observer.observe(container, { childList: true, subtree: true });
    syncPromise();
    if (window.customElements?.whenDefined) {
      window.customElements.whenDefined('delivery-promise-wc').then(syncPromise);
    }
  };

  const init = () => {
    document.querySelectorAll('[data-product-v3]').forEach(root => {
      if (root.dataset.v3Initialized === 'true') return;
      root.dataset.v3Initialized = 'true';
      new ProductV3(root);
      alignShopPromiseSummary(root);
    });
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
